import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, map, Observable, race, take } from 'rxjs';
import { z } from 'zod';
import moment from 'moment-timezone';
import { api } from '@consts/url.const';
import { IntelligenceModels } from '../store/models/intelligence.model';
import { IntelligenceResultsService } from './intelligence-results.service';
import { CaseManagementModel } from '@models/case-management.model';
import { CustomEventModel } from '@models/custom-event.model';
import { WallV2Model } from '@models/wall-v2.model';
import { AlertCategory, EventV2Document, SystemType } from '@models/alerts-v2.model';
import { ArchiveStatus } from '@models/archive.model';
import { CameraEditSelectors } from '@states/camera-edit/camera-edit.selector-types';
import { AccessDoorsSelectors } from '@states/access-doors/access-doors.selector-types';
import { CustomObjectStatesSelectors } from '@states/custom-objects-states/custom-objects-states.selector-types';
import { DoorsSelectors } from '@states/doors/doors.selector-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { EdgeSelectors } from '@states/edge/edge.selector-types';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import * as EdgeActions from '@states/edge/edge.actions';
import { ConfirmDialogModel, ConfirmDialogService } from '@ui';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { PermissionModel } from '@models/permission.model';
import { PermissionSelectors } from '@states/permissions/permissions.selector-types';
import { ShelfsSelectors } from '@states/shelfs/shelfs.selector-types';
import { resolveAlertTemplate } from '../shared/modals/search-global-modal/components/alerts/alert-type-filter.util';
import { LocationsService } from '../locations/locations.service';
import { PanicSystemConfigurationService } from '../development/panic-system-configuration.service';
import { LocationPickerModalComponent } from '@modals/location-picker-modal/location-picker-modal.component';
import { OrgVariablesActions } from '@states/org-variables/org-variables.action-types';
import { OrgVariablesModel } from '@models/org-variables.model';
import { ZoneRegionsService } from './zone-regions.service';
import { ZoneRegionsActions } from '@states/zone-regions/zone-regions.actions';
import { ZoneRegionsModels } from '@share-models';

/** `AddLocationStep.ADD_EDGE`; keep this dependency out of the tool service. */
const ADD_CORE_STEP = 2;

/**
 * A time string is only unambiguous if it carries an explicit offset or Z.
 * Note moment's strict ISO_8601 accepts "2026-07-23 15:42", so it cannot be
 * used for this test: passing that through would hand the backend a naive time
 * and silently shift the clip by the local offset.
 */
const ABSOLUTE_TIME_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/;

/** Timezone-less shapes an LLM tends to emit for a resolved date/time. */
const NAIVE_TIME_FORMATS = [
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DDTHH:mm',
  'YYYY-MM-DD',
];

const PREPARED_WALL_DRAFTS_MAX_SIZE = 10;

// ─────────────────────────────────────────────────────────────────────────────
// Architecture: Every tool follows Pattern A — "Preview panel" tool.
//
//   Backend  : business logic in src/<feature>/<feature>.service.ts
//              MCP registration in src/<feature>/<feature>.tool.ts (for Claude Desktop, etc.)
//              REST endpoint in src/<feature>/<feature>.controller.ts (for Angular)
//   Frontend : tool handler calls the REST endpoint via HttpClient
//              (auth interceptor + token refresh included for free), then pushes
//              data to IntelligenceResultsService so the preview panel updates.
//
// To add a new tool:
//   1. Create backend/<feature>.service.ts + .tool.ts + .controller.ts + .module.ts
//   2. Register in mcp.module.ts (imports) and mcp.service.ts (register call)
//   3. Add frontend tool method here and include it in getFrontendTools()
//   4. Add preview component (or use existing one for same data shape)
//   5. Add result type to IntelligenceResultsService and intelligence.model.ts
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class IntelligenceToolsService {
  private readonly httpClient = inject(HttpClient);
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly panicSystemConfigurationService = inject(PanicSystemConfigurationService);
  private readonly store$ = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly locationsService = inject(LocationsService);
  private readonly dialog = inject(MatDialog);
  private readonly actions$ = inject(Actions);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly zoneRegionsService = inject(ZoneRegionsService);
  private readonly hasProtectiveGear = this.store$.selectSignal(
    CameraEditSelectors.selectOrgProtectiveGear,
  );
  private readonly savedDoors = this.store$.selectSignal(
    DoorsSelectors.selectSavedDoors,
  );
  private readonly hasShelves = this.store$.selectSignal(
    ShelfsSelectors.hasShelves,
  );
  private readonly isDeveloper = this.store$.selectSignal(
    OrganizationSelectors.isDeveloper,
  );
  private readonly permissions = this.store$.selectSignal(
    PermissionSelectors.selectPermissions,
  );
  private readonly hasAccessDoors = this.store$.selectSignal(
    AccessDoorsSelectors.selectHasAccessDoors,
  );
  private readonly activeOrganization = this.store$.selectSignal(
    OrganizationSelectors.selectActiveOrganization,
  );
  private readonly shelvesEnabled = this.store$.selectSignal(
    OrganizationSelectors.selectEnableShelves,
  );
  private readonly objectStates = this.store$.selectSignal(
    CustomObjectStatesSelectors.selectAllCustomObjectStates,
  );
  private readonly physicalAccessControls = this.store$.selectSignal(
    LocationSelectors.selectPhysicalAccessControls,
  );
  private readonly faceRecognitionEnabled = this.store$.selectSignal(
    LocationSelectors.isFaceRecognitionEnabled,
  );
  private readonly enablePalestinianLicensePlate = this.store$.selectSignal(
    OrganizationSelectors.selectEnablePalestinianLicensePlate,
  );
  private readonly disableMissingObjectEventConfiguration = this.store$.selectSignal(
    OrganizationSelectors.selectDisableMissingObjectEventConfiguration,
  );
  private readonly preparedWallDrafts = new Map<
    string,
    IntelligenceModels.Walls.Create.PreparedDraft
  >();
  private addCameraRequestSequence: number = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getFrontendTools(): any[] {
    return [
      this.organizationMembersTool(),
      this.deviceHealthTool(),
      this.deviceListTool(),
      this.locationsTool(),
      this.personsTool(),
      this.personGroupsTool(),
      this.personGroupMembersTool(),
      this.dashboardsTool(),
      this.vehiclesTool(),
      this.vehicleGroupsTool(),
      this.vehicleGroupMembersTool(),
      this.variablesTool(),
      this.currentUserInfoTool(),
      this.organizationInfoTool(),
      this.userOrganizationsTool(),
      this.listTagsTool(),
      this.prepareCreateTagTool(),
      this.createTagTool(),
      this.listEventTagsTool(),
      this.createEventTagTool(),
      this.listTimeSchedulesTool(),
      this.listCaseCategoriesTool(),
      this.createTimeScheduleTool(),
      this.smartSearchTool(),
      this.expandSearchFaceTool(),
      this.expandSearchAppearanceTool(),
      this.createAlertConfigurationTool(),
      this.alertsTool(),
      this.getAlertTool(),
      this.setAlertConfigurationEnabledTool(),
      this.alertConfigurationsTool(),
      this.caseManagerTool(),
      this.archiveTool(),
      this.prepareCreateArchiveTool(),
      this.createArchiveTool(),
      this.pickCameraTool(),
      this.pickCamerasTool(),
      this.addCameraTool(),
      this.addCoreTool(),
      this.deleteCameraTool(),
      this.deleteCoreTool(),
      this.viewWallTool(),
      this.wallsTool(),
      this.prepareCreateWallTool(),
      this.createWallTool(),
      this.reviewWallDraftTool(),
      this.objectDetectionsByHourTool(),
      this.objectDetectionsByCameraTool(),
      this.objectGroupsByHourTool(),
      this.objectTypesByHourTool(),
      this.objectTypesByCameraTool(),
      this.alertsByHourTool(),
      this.alertsByCameraTool(),
      this.alertTypesDistributionTool(),
      this.listZoneRegionsTool(),
      this.getZoneRegionTool(),
      this.deleteZoneRegionTool(),
      this.listCustomObjectsTool(),
      this.createCustomObjectTool(),
    ];
  }

  // ── list-custom-objects ───────────────────────────────────────────────────

  /**
   * Fetch the organization's custom objects — both the appearance-trained
   * standard objects and the state-based ones — in one round trip. Shared by the
   * `list-custom-objects` tool handler and the custom-objects-result panel.
   */
  public async fetchCustomObjects(): Promise<IntelligenceModels.CustomObjects.ListResponse> {
    return firstValueFrom(
      this.httpClient.get<IntelligenceModels.CustomObjects.ListResponse>(
        api.intelligence.customObjects,
      ),
    );
  }

  private listCustomObjectsTool() {
    return {
      name: 'list-custom-objects',
      description:
        'Fetch every organization custom object and show them in the results panel. ' +
        'Custom objects are what the Database → Custom objects page defines: both ' +
        'appearance-trained standard objects and state-based ones (for example a door that is ' +
        'open or closed). ' +
        'Use for requests such as "show me our custom objects", "list the custom objects" or ' +
        '"which custom objects do we have?". ' +
        'This tool is read-only: it creates, renames, retrains and deletes nothing. ' +
        'The panel shows the custom object cards — NEVER enumerate custom object names in the reply. ' +
        'Reply with one concise sentence stating the total, e.g. "You have 6 custom objects." ' +
        'If the list is empty, say that no custom objects exist yet. ' +
        'Do not use for requests to create a custom object; use create-custom-object.',
      parameters: z.object({}),
      handler: async (): Promise<IntelligenceModels.CustomObjects.ListToolResponse> => {
        const response = await this.fetchCustomObjects();
        this.resultsService.setCustomObjects(response, `${response.count} custom objects`);
        // Only the total and the message reach the model; the cards are the panel's job.
        return {
          panelUpdated: true,
          count: response.count,
          message: response.message,
        };
      },
    };
  }

  // ── create-custom-object ──────────────────────────────────────────────────

  /**
   * Open the Database page's creation wizard over the custom-objects panel.
   *
   * The MCP twin routes its own widget to a create view on a `draftCreated`
   * marker; here the wizard is a dialog, so the listing is published as the panel
   * result and the panel is asked to open the dialog over it. Nothing is created
   * by the tool — the user still has to finish the wizard and save.
   */
  private createCustomObjectTool() {
    return {
      name: 'create-custom-object',
      description:
        'Open the custom-object creation wizard in the preview panel. ' +
        'Use for every request to create, add, define or train a new custom object — for example ' +
        '"create a custom object for a red forklift", "add a custom object", "train a new object". ' +
        "Provide prompt only when the user described the object to detect. Preserve the user's exact " +
        "wording; never translate, shorten or invent a description. It only prefills the wizard's " +
        'description field, which the user can still edit. ' +
        'Do not use list-custom-objects for creation requests. ' +
        'This tool creates nothing: the user must complete the wizard and save it. ' +
        'After calling, tell the user the creation wizard is open and that nothing has been created yet.',
      parameters: z.object({
        prompt: z
          .string()
          .optional()
          .describe(
            'Verbatim description of the object to detect, as the user said it. Do not translate, shorten or invent one.',
          ),
      }),
      handler: async (
        args: IntelligenceModels.CustomObjects.CreateParams,
      ): Promise<IntelligenceModels.CustomObjects.CreateToolResponse> => {
        const response = await this.fetchCustomObjects();
        this.resultsService.requestCreateCustomObject(
          response,
          args.prompt?.trim() || undefined,
          this.translateService.instant('CUSTOM_OBJECTS.CREATE_CUSTOM_OBJECT'),
        );
        return {
          panelUpdated: true,
          message:
            'The custom-object creation wizard is open in the panel. Nothing has been created yet; ' +
            'the user must finish the wizard and save it.',
        };
      },
    };
  }

  // ── Zone regions ────────────────────────────────────────────────────────

  public async fetchZoneRegions(
    filtersOrQuery: ZoneRegionsModels.Filters | string = {},
  ): Promise<IntelligenceModels.ZoneRegions.ListResponse> {
    const filters: ZoneRegionsModels.Filters =
      typeof filtersOrQuery === 'string'
        ? { query: filtersOrQuery }
        : filtersOrQuery;
    const normalizedQuery = filters.query?.trim() ?? '';
    const normalizedFilters: ZoneRegionsModels.Filters = {
      ...filters,
      query: normalizedQuery || undefined,
    };
    const [zones, cameras] = await Promise.all([
      firstValueFrom(
        this.zoneRegionsService.getZoneRegions(normalizedFilters, null),
      ),
      firstValueFrom(this.zoneRegionsService.getAllZoneRegionCamerasForPicker()),
    ]);

    return {
      view: 'list',
      count: zones.length,
      query: normalizedQuery,
      zoneRegions: zones,
      cameraCounts: Object.fromEntries(
        zones.map((zone) => [
          zone._id,
          cameras.filter((camera) => camera.zoneRegionId === zone._id).length,
        ]),
      ),
    };
  }

  public async fetchZoneRegionDetail(args: {
    zoneRegionId?: string;
    name?: string;
  }): Promise<IntelligenceModels.ZoneRegions.ResolveResponse> {
    const zones = await firstValueFrom(this.zoneRegionsService.getAllZoneRegionsShallow());
    const normalizedName = args.name?.trim().toLocaleLowerCase();
    const matches = zones.filter(
      (zone) =>
        zone._id === args.zoneRegionId ||
        (!!normalizedName && zone.name.trim().toLocaleLowerCase() === normalizedName),
    );

    if (matches.length !== 1) {
      return {
        needsInput: true,
        message: matches.length
          ? 'More than one zone region matches. Ask the user for the exact name.'
          : 'No matching zone region was found.',
      };
    }

    const zone = matches[0];
    const [zoneCameras, cameras] = await Promise.all([
      firstValueFrom(this.zoneRegionsService.getZoneRegionCameras(zone._id)),
      firstValueFrom(this.store$.select(CameraSelectors.selectAllCameras).pipe(take(1))),
    ]);
    const cameraNames = Object.fromEntries(zoneCameras.map((item) => {
      const camera = cameras.find(
        (candidate) =>
          (candidate.edgeOnly?.cameraId ?? candidate.cameraId) === item.cameraId &&
          candidate.edgeId === item.edgeId &&
          candidate.locationId === item.locationId,
      );
      return [item._id, camera?.edgeOnly?.name ?? camera?.name ?? 'Unavailable camera'];
    }));

    return {
      data: {
        view: 'detail',
        zoneRegion: zone,
        cameras: zoneCameras,
        cameraNames,
      },
    };
  }

  public async requestZoneRegionDeletion(args: {
    zoneRegionId?: string;
    name?: string;
  }): Promise<IntelligenceModels.ZoneRegions.DeleteOutcome> {
    const resolved = await this.fetchZoneRegionDetail(args);
    if ('needsInput' in resolved) return { conflict: true };
    const zone = resolved.data.zoneRegion;
    const confirmation = await firstValueFrom(
      this.confirmDialog.open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translateService.instant('ZONE_REGIONS.DIALOGS.DELETE.TITLE'),
        msg: this.translateService.instant('ZONE_REGIONS.DIALOGS.DELETE.MESSAGE', {
          name: `"${zone.name}"`,
        }),
        confirm: this.translateService.instant('COMMON.DELETE'),
        cancel: this.translateService.instant('COMMON.CANCEL'),
        disableClose: true,
      }),
    );
    if (confirmation?.selection !== ConfirmDialogModel.ConfirmResult.OK) return { cancelled: true };

    const completed = firstValueFrom(
      race(
        this.actions$.pipe(
          ofType(ZoneRegionsActions.deleteZoneRegionSuccess),
          filter((action) => action.id === zone._id),
          map(() => true),
        ),
        this.actions$.pipe(ofType(ZoneRegionsActions.deleteZoneRegionFail), map(() => false)),
      ).pipe(take(1)),
    );
    this.store$.dispatch(ZoneRegionsActions.startDeleteZoneRegion({ id: zone._id }));
    if (!(await completed)) return { error: true };
    return { deleted: true, id: zone._id, name: zone.name };
  }

  private listZoneRegionsTool() {
    return {
      name: 'list-zone-regions',
      description:
        'List and display the organization\'s zone regions in the preview panel. Use query only for a zone-region name search and cameraNames for camera filters. Resolve camera names through the existing organization camera state; never put a camera name in query. Never describe polygon coordinates or internal IDs.',
      parameters: z.object({
        query: z.string().optional().describe('Optional zone-region name search.'),
        cameraNames: z
          .array(z.string())
          .optional()
          .describe('Optional exact camera names. A region must be associated with a selected camera.'),
      }),
      handler: async ({ query, cameraNames }: { query?: string; cameraNames?: string[] }) => {
        const requestedCameraNames = (cameraNames ?? [])
          .map((name) => name.trim())
          .filter(Boolean);
        const cameraFilters: NonNullable<
          IntelligenceModels.ZoneRegions.ListResponse['cameraFilters']
        > = [];

        if (requestedCameraNames.length) {
          const cameras = await firstValueFrom(
            this.store$.select(CameraSelectors.selectAllCameras).pipe(take(1)),
          );
          for (const requestedName of requestedCameraNames) {
            const normalizedName = requestedName.toLocaleLowerCase();
            const matches = cameras.filter(
              (camera) =>
                (camera.edgeOnly?.name ?? camera.name)?.trim().toLocaleLowerCase() ===
                normalizedName,
            );
            if (matches.length !== 1) {
              return {
                needsInput: true,
                message: matches.length
                  ? `More than one camera is named "${requestedName}". Ask the user to choose the camera.`
                  : `No camera named "${requestedName}" was found. Ask the user for the exact camera name.`,
              };
            }
            const camera = matches[0];
            const cameraId = camera.edgeOnly?.cameraId ?? camera.cameraId;
            if (!cameraId || !camera.edgeId) {
              return {
                needsInput: true,
                message: `Camera "${requestedName}" cannot be used as a filter. Ask the user to choose another camera.`,
              };
            }
            if (!cameraFilters.some((filter) => filter.cameraId === cameraId)) {
              cameraFilters.push({
                cameraId,
                edgeId: camera.edgeId,
                locationId: camera.locationId,
                name: camera.edgeOnly?.name ?? camera.name ?? requestedName,
              });
            }
          }
        }

        const response = await this.fetchZoneRegions({
          query,
          cameraIdsIncludes: cameraFilters.length
            ? cameraFilters.map(({ cameraId }) => cameraId)
            : undefined,
        });
        if (cameraFilters.length) response.cameraFilters = cameraFilters;
        const labelParts = [
          `${response.count} zone regions`,
          response.query,
          ...cameraFilters.map(({ name }) => name),
        ].filter(Boolean);
        const label = labelParts.join(' · ');
        this.resultsService.setZoneRegions(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          ...(cameraFilters.length
            ? { cameraNames: cameraFilters.map(({ name }) => name) }
            : {}),
          zoneRegions: response.zoneRegions.map(({ _id, name, countingEnabled }) => ({
            name,
            countingEnabled: !!countingEnabled,
            cameraCount: response.cameraCounts[_id] ?? 0,
          })),
        };
      },
    };
  }

  private getZoneRegionTool() {
    return {
      name: 'get-zone-region',
      description: 'Display one zone region and its associated cameras in the preview panel. Use after listing zone regions or when the user names one. Never expose shape coordinates or internal IDs in the reply.',
      parameters: z.object({ zoneRegionId: z.string().optional(), name: z.string().optional() }),
      handler: async (args: { zoneRegionId?: string; name?: string }) => {
        const response = await this.fetchZoneRegionDetail(args);
        if ('needsInput' in response) return response;
        this.resultsService.setZoneRegions(response.data, response.data.zoneRegion.name);
        return {
          panelUpdated: true,
          name: response.data.zoneRegion.name,
          countingEnabled: !!response.data.zoneRegion.countingEnabled,
          cameras: response.data.cameras.map((camera) => ({
            name: response.data.cameraNames[camera._id] ?? 'Unavailable camera',
          })),
        };
      },
    };
  }

  private deleteZoneRegionTool() {
    return {
      name: 'delete-zone-region',
      description: 'Delete exactly one zone region only when the user explicitly requests it. The native confirmation dialog is mandatory; never delete an ambiguous name.',
      parameters: z.object({ zoneRegionId: z.string().optional(), name: z.string().optional() }),
      handler: async (args: { zoneRegionId?: string; name?: string }) => {
        const outcome = await this.requestZoneRegionDeletion(args);
        if (!('deleted' in outcome)) return outcome;
        const response = await this.fetchZoneRegions();
        this.resultsService.setZoneRegions(response, `${response.count} zone regions`);
        return { deleted: true, name: outcome.name, panelUpdated: true };
      },
    };
  }

  // ── list-case-manager-cases ──────────────────────────────────────────────

  public async fetchCaseManager(
    args: IntelligenceModels.CaseManager.ListParams,
  ): Promise<IntelligenceModels.CaseManager.Response> {
    let params = new HttpParams();
    if (args.limit) params = params.set('limit', args.limit);
    if (args.query?.trim()) params = params.set('query', args.query.trim());
    if (args.latestTs) params = params.set('latestTs', args.latestTs);
    args.statuses?.forEach((status) => {
      params = params.append('statuses', status);
    });
    args.tags?.forEach((tag) => {
      params = params.append('tags', tag);
    });

    return firstValueFrom(
      this.httpClient.get<IntelligenceModels.CaseManager.Response>(
        api.intelligence.caseManager,
        { params },
      ),
    );
  }

  private caseManagerTool() {
    return {
      name: 'list-case-manager-cases',
      description:
        'Fetch and display case-manager cases visible to the current user in the preview panel. ' +
        'Use for any question about cases, incidents, case manager, open cases, closed cases, or case ownership. ' +
        'Supports filtering by case name query, statuses, tag IDs, latest timestamp, and limit. ' +
        'After calling this tool ALWAYS write a short text reply summarising what was found.',
      parameters: z.object({
        limit: z
          .number()
          .int()
          .positive()
          .optional()
          .describe('Maximum number of cases to return. Default 50.'),
        query: z.string().optional().describe('Case name search query.'),
        statuses: z
          .array(z.nativeEnum(CaseManagementModel.CaseStatus))
          .optional()
          .describe('Case statuses to include: Active or Closed.'),
        tags: z
          .array(z.string())
          .optional()
          .describe('Case tag IDs to include.'),
        latestTs: z
          .number()
          .positive()
          .optional()
          .describe('Return cases created before this timestamp in ms.'),
      }),
      handler: async (args: IntelligenceModels.CaseManager.ListParams) => {
        const response = await this.fetchCaseManager(args);
        const label = `${response.count} cases${response.filter.query ? ` · ${response.filter.query}` : ''}`;
        this.resultsService.setCaseManager(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          filter: response.filter,
          message: response.message,
        };
      },
    };
  }

  // ── list-archives ─────────────────────────────────────────────────────────

  /**
   * Fetch one page of archives from the intelligence endpoint. Shared by the
   * `list-archives` tool handler and the archive-result component's infinite
   * scroll. `hasMore` is derived client-side: a full page means more may exist.
   */
  public async fetchArchives(
    args: IntelligenceModels.Archive.FetchArchivesParams,
  ): Promise<IntelligenceModels.Archive.Response> {
    let params = new HttpParams();
    if (args.limit) params = params.set('limit', args.limit);
    if (args.query?.trim()) params = params.set('query', args.query.trim());
    if (args.latestTs) params = params.set('latestTs', args.latestTs);
    if (args.cameraName?.trim()) params = params.set('cameraName', args.cameraName.trim());
    if (args.cameraIds?.length) params = params.set('cameraIds', args.cameraIds.join(','));
    if (args.statuses?.length) params = params.set('statuses', args.statuses.join(','));
    if (args.types?.length) params = params.set('types', args.types.join(','));
    const start = args.start ?? args.startMs;
    const end = args.end ?? args.endMs;
    if (start) params = params.set('start', start);
    if (end) params = params.set('end', end);

    const response = await firstValueFrom(
      this.httpClient.get<IntelligenceModels.Archive.Response>(
        api.intelligence.archive,
        {
          params,
        },
      ),
    );

    return { ...response, hasMore: response.count >= response.filter.limit };
  }

  private archiveTool() {
    return {
      name: 'list-archives',
      description:
        "Fetch and display the organization's video archives (saved recordings, exported clips, timelapses) in the preview panel. " +
        'Use for any question about archives, saved recordings, exported clips, or timelapses. ' +
        'Supports an archive name query, a result limit, and a latestTs cursor for pagination. ' +
        'After calling this tool ALWAYS write a short text reply summarising what was found.',
      parameters: z.object({
        limit: z
          .number()
          .int()
          .positive()
          .optional()
          .describe('Maximum number of archives to return. Default 100.'),
        query: z.string().optional().describe('Archive name search query.'),
        latestTs: z.number().positive().optional().describe('Return archives created before this timestamp in ms.'),
        cameraName: z
          .string()
          .optional()
          .describe(
            'Camera name is a hint; the tool resolves it against organization cameras itself — do NOT call get-locations first. An unknown camera returns empty with unresolvedCameraNames.',
          ),
        statuses: z
          .array(z.nativeEnum(ArchiveStatus))
          .optional()
          .describe('Archive status codes to include.'),
        types: z
          .array(z.nativeEnum(IntelligenceModels.Archive.ListType))
          .optional()
          .describe('Archive types to include: archive, timelapse, or clip.'),
        startMs: z.number().int().positive().optional().describe('Inclusive createdAt lower bound in epoch ms.'),
        endMs: z.number().int().positive().optional().describe('Inclusive createdAt upper bound in epoch ms.'),
      }),
      handler: async (args: IntelligenceModels.Archive.ListArchivesToolParams) => {
        const response = await this.fetchArchives(args);
        const label = `${response.count} archives${response.filter.query ? ` · ${response.filter.query}` : ''}`;
        this.resultsService.setArchive(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          filter: response.filter,
          message: response.message,
        };
      },
    };
  }

  /**
   * Coerce a model-supplied time into something the endpoint accepts.
   *
   * Deliberately narrow. A strict ISO string or a number passes straight
   * through; a timezone-less string ("2026-07-23 15:42") or a `{date, time}`
   * object — both observed from the model — is reinterpreted in the user's
   * timezone, which is the timezone the model was told to resolve against.
   * Anything else is passed through untouched, so the backend still rejects it
   * and the model gets the error plus recovery hints instead of this quietly
   * inventing a time.
   */
  private toAbsoluteTime(value: unknown): unknown {
    if (typeof value === 'number') return value;

    if (typeof value === 'string') {
      const trimmed = value.trim();

      return ABSOLUTE_TIME_PATTERN.test(trimmed) ? trimmed : this.toLocalIso(trimmed);
    }

    if (value && typeof value === 'object') {
      const { date, time } = value as { date?: unknown; time?: unknown };
      if (typeof date === 'string') {
        return this.toLocalIso(`${date} ${typeof time === 'string' ? time : '00:00:00'}`);
      }
    }

    return value;
  }

  private toLocalIso(value: string): string {
    const parsed = moment(value, NAIVE_TIME_FORMATS, true);

    return parsed.isValid() ? parsed.format() : value;
  }

  /**
   * The wall-clock context the Cmd+K chat runtime never supplies. Read per tool
   * call, since the tool list itself is built once at app bootstrap.
   */
  private currentTimeContext(): Required<
    Pick<IntelligenceModels.Archive.Create.PrepareToolResponse, 'currentTime' | 'timezone'>
  > {
    return { currentTime: moment().format(), timezone: moment.tz.guess() };
  }

  /**
   * Turn a failed archive call into the structured error the MCP tools return,
   * so the model can correct itself instead of the chat turn dying on a
   * rejected promise. Nest validation detail lives in `error.errors`.
   */
  private buildArchiveToolError(
    error: unknown,
    recovery: string[],
  ): IntelligenceModels.Archive.Create.Error {
    const body =
      error instanceof HttpErrorResponse
        ? (error.error as { message?: string; errors?: string[] } | null)
        : null;
    const message = body?.errors?.length
      ? body.errors.join('; ')
      : (body?.message ??
        (error instanceof Error ? error.message : 'Could not reach the archive service.'));

    return {
      code: IntelligenceModels.Archive.Create.ErrorCode.Upstream,
      message,
      recovery,
    };
  }

  private prepareCreateArchiveTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.Archive.Create.Params,
    IntelligenceModels.Archive.Create.PrepareToolResponse
  > {
    return {
      name: IntelligenceModels.Archive.Create.ToolName.Prepare,
      description: IntelligenceModels.Archive.Create.PREPARE_CREATE_TOOL_DESCRIPTION,
      parameters: z.object({
        mode: z
          .nativeEnum(IntelligenceModels.Archive.Create.Mode)
          .optional()
          .describe(
            "archive for an exported clip, or timelapse. If the user's request already names archive or timelapse (in any language), always pass it here — do not re-ask.",
          ),
        cameraId: z.string().optional().describe('Exact camera ID. Preferred when known.'),
        cameraName: z
          .string()
          .optional()
          .describe(
            'Camera name words; resolved case-insensitively by exact name, whole-token, then substring matching.',
          ),
        // A single scalar type on purpose. The MCP tool uses
        // z.union([string, number]), which becomes an `anyOf` in the tool schema
        // — models satisfy that poorly and were observed inventing objects
        // instead ({hour,minute,daysAgo}, then {date,time}), each a 400 from the
        // DTO validator. One unambiguous string is far easier to hit, and epoch
        // milliseconds buy the model nothing here.
        startTime: z
          .string()
          .optional()
          .describe(
            'Absolute start as a single ISO-8601 string with timezone offset, e.g. "2026-07-23T15:42:00+02:00". Must be a string, never an object. Resolve wording like "yesterday at 15:42" against the current time before calling.',
          ),
        endTime: z
          .string()
          .optional()
          .describe(
            'Absolute end as a single ISO-8601 string with timezone offset, e.g. "2026-07-23T15:52:00+02:00". Must be a string, never an object.',
          ),
        name: z
          .string()
          .optional()
          .describe(
            'Archive name using only letters, numbers, spaces, and underscores; asked via needsInput when absent ("Untitled archive" preset or a suggested camera-based name; the user may always type their own).',
          ),
        outputDurationMinutes: z
          .number()
          .int()
          .positive()
          .optional()
          .describe('Timelapse output duration in whole minutes; defaults to 1.'),
      }),
      handler: async (
        args: IntelligenceModels.Archive.Create.Params,
      ): Promise<IntelligenceModels.Archive.Create.PrepareToolResponse> => {
        const payload: IntelligenceModels.Archive.Create.Params = {
          ...args,
          ...(args.startTime !== undefined
            ? { startTime: this.toAbsoluteTime(args.startTime) as string | number }
            : {}),
          ...(args.endTime !== undefined
            ? { endTime: this.toAbsoluteTime(args.endTime) as string | number }
            : {}),
        };

        try {
          const result = await firstValueFrom(
            this.httpClient.post<IntelligenceModels.Archive.Create.PrepareResponse>(
              api.intelligence.archivePrepareCreate,
              payload,
            ),
          );
          return { ...result, ...this.currentTimeContext() };
        } catch (error: unknown) {
          return {
            error: this.buildArchiveToolError(error, [
              'startTime and endTime must be an absolute ISO-8601 string with offset, or epoch milliseconds — never an object or a relative expression.',
              'Resolve the user\'s wording against currentTime/timezone below, then call prepare-create-archive again.',
            ]),
            ...this.currentTimeContext(),
          };
        }
      },
    };
  }

  private createArchiveTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.Archive.Create.ExecuteParams,
    IntelligenceModels.Archive.Create.ToolResponse
  > {
    return {
      name: IntelligenceModels.Archive.Create.ToolName.Create,
      description: IntelligenceModels.Archive.Create.CREATE_TOOL_DESCRIPTION,
      parameters: z.object({
        preparation: z
          .string()
          .min(1)
          .describe('Preparation token returned by prepare-create-archive.'),
      }),
      handler: async (
        args: IntelligenceModels.Archive.Create.ExecuteParams,
      ): Promise<IntelligenceModels.Archive.Create.ToolResponse> => {
        let result: IntelligenceModels.Archive.Create.ExecuteResponse;
        try {
          result = await firstValueFrom(
            this.httpClient.post<IntelligenceModels.Archive.Create.ExecuteResponse>(
              api.intelligence.archiveCreate,
              args,
            ),
          );
        } catch (error: unknown) {
          return {
            error: this.buildArchiveToolError(error, [
              'Check the archives list before retrying, in case it was created.',
              'If the preparation token expired, call prepare-create-archive again to get a fresh one.',
            ]),
          };
        }
        if (!result.created) return result;
        // Defensive rollout guard: an older/malformed backend response must not let the
        // model turn HTTP acceptance into a user-visible "successfully created" claim.
        if (!result.created.archiveId) {
          return {
            error: {
              code: IntelligenceModels.Archive.Create.ErrorCode.Upstream,
              message:
                'The create request returned without a confirmed archive record. The archive was not successfully created.',
              recovery: [
                'Check the archives list in case processing completes later.',
                'Retry only after confirming that no matching archive exists.',
              ],
            },
          };
        }

        let panelUpdated = false;
        try {
          const response = await this.fetchArchives({});
          const highlightedResponse: IntelligenceModels.Archive.Response = {
            ...response,
            ...(result.created.sessionId
              ? { highlightedSessionId: result.created.sessionId }
              : {}),
          };
          const label = `${response.count} archives${response.filter.query ? ` · ${response.filter.query}` : ''}`;
          this.resultsService.setArchive(highlightedResponse, label);
          panelUpdated = true;
        } catch {
          // The archive is already confirmed by archiveId. A secondary panel refresh
          // must not turn a successful creation into a retry that can duplicate work.
        }

        return {
          panelUpdated,
          created: result.created,
          ...(result.assistant ? { assistant: result.assistant } : {}),
        };
      },
    };
  }

  private pickCameraTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.Locations.PickCameraToolParams,
    IntelligenceModels.Locations.PickCameraOutcome
  > {
    return {
      name: IntelligenceModels.Locations.ToolName.PickCamera,
      description:
        'Open an interactive camera selector in the panel so the user can choose a camera. The selection returns its cameraId and cameraName; treat that id as the camera choice for whatever flow requested the selection and continue that flow with it. If instead it returns { cancelled: true }, the user dismissed the selector without choosing: abandon the flow that needed a camera, do not retry the selector, and simply acknowledge that nothing was selected. Use only when a flow needs the user to choose a camera interactively (none or several matches); never as a substitute for tools that resolve cameras themselves.',
      parameters: z.object({
        query: z
          .string()
          .optional()
          .describe(
            "Pre-filter the selector by location, sub-location, or camera name — e.g. the user's camera phrase that failed to resolve.",
          ),
        purpose: z
          .string()
          .optional()
          .describe(
            'One short line shown in the panel header, e.g. "Select a camera for the archive".',
          ),
      }),
      handler: async (
        args: IntelligenceModels.Locations.PickCameraToolParams,
      ): Promise<IntelligenceModels.Locations.PickCameraOutcome> => {
        this.resultsService.setPickCamera(args, args.purpose ?? '');
        // The selector can also be torn down without an answer (back to search,
        // new chat, palette closed). Racing the cancellation stream is what
        // keeps this handler from awaiting forever in that case.
        return firstValueFrom(
          race(
            this.resultsService.cameraPicked$,
            this.resultsService.cameraPickCancelled$,
          ).pipe(take(1)),
        );
      },
    };
  }

  private pickCamerasTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.Locations.PickCameraToolParams,
    IntelligenceModels.Locations.PickCamerasOutcome
  > {
    return {
      name: IntelligenceModels.Locations.ToolName.PickCameras,
      description:
        'Open an interactive multi-camera selector in the panel so the user can choose cameras and press Done. The selection returns cameras as cameraId and cameraName pairs; pass those IDs verbatim to the flow that requested them. An empty cameras array means the user chose no cameras, so continue the flow with noCameras true. If instead it returns { cancelled: true }, the user dismissed the selector: abandon the flow that needed cameras, do not retry the selector, and acknowledge that nothing was selected.',
      parameters: z.object({
        query: z
          .string()
          .optional()
          .describe(
            "Pre-filter the selector by location, sub-location, or camera name — e.g. the user's camera phrase that failed to resolve.",
          ),
        purpose: z
          .string()
          .optional()
          .describe(
            'One short line shown in the panel header, e.g. "Select cameras for the wall".',
          ),
      }),
      handler: async (
        args: IntelligenceModels.Locations.PickCameraToolParams,
      ): Promise<IntelligenceModels.Locations.PickCamerasOutcome> => {
        this.resultsService.setPickCamera({ ...args, multi: true }, args.purpose ?? '');
        return firstValueFrom(
          race(
            this.resultsService.camerasPicked$,
            this.resultsService.cameraPickCancelled$,
          ).pipe(take(1)),
        );
      },
    };
  }

  private addCameraTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.CameraProvisioning.AddCameraToolParams,
    IntelligenceModels.CameraProvisioning.AddCameraOutcome
  > {
    return {
      name: IntelligenceModels.CameraProvisioning.ToolName.AddCamera,
      description:
        'Open the existing Add Camera wizard in a dialog. Use when the user asks to add a camera. You may provide locationName and/or coreName when the user explicitly gave them; these are matched against the current organization and unique matches are prefilled. Do not provide a Core serial number or a detection method: neither is supported here. If this returns { conflict: true }, ask the user to clarify which Location or Core they intended, then call again. If this returns { cancelled: true }, the user dismissed the wizard: abandon the add-camera flow, do not retry automatically, and acknowledge that no camera was added. When it returns { created: true }, confirm the camera was added to its location and offer to add another camera to the same Core or open that location.',
      parameters: z.object({
        locationName: z
          .string()
          .optional()
          .describe('Location name explicitly stated by the user.'),
        coreName: z
          .string()
          .optional()
          .describe('Core name explicitly stated by the user.'),
      }),
      handler: async (
        args: IntelligenceModels.CameraProvisioning.AddCameraToolParams,
      ): Promise<IntelligenceModels.CameraProvisioning.AddCameraOutcome> => {
        const [locations, edges] = await Promise.all([
          firstValueFrom(this.store$.select(LocationSelectors.selectAllLocations).pipe(take(1))),
          firstValueFrom(this.store$.select(EdgeSelectors.selectAllEdges).pipe(take(1))),
        ]);
        const grounding = IntelligenceModels.CameraProvisioning.resolveAddCameraRequest(
          args,
          locations,
          edges,
          this.nextAddCameraRequestId(),
        );
        if (grounding.kind === IntelligenceModels.CameraProvisioning.GroundingResultKind.Conflict) {
          return { conflict: true };
        }

        const request = grounding.request;
        if (!request) return { conflict: true };
        this.resultsService.setAddCamera(
          request,
          args.coreName ?? args.locationName ?? this.translateService.instant('SEARCH_GLOBAL_MODAL.ADD_CAMERA'),
        );
        return firstValueFrom(
          race(
            this.resultsService.addCameraFinished$.pipe(
              filter((event) => event.requestId === request.requestId),
            ),
            this.resultsService.addCameraCancelled$.pipe(
              filter((event) => event.requestId === request.requestId),
            ),
          ).pipe(
            take(1),
          ),
        ).then((event) => event.outcome);
      },
    };
  }

  private nextAddCameraRequestId(): string {
    this.addCameraRequestSequence += 1;
    return `add-camera-${this.addCameraRequestSequence}`;
  }

  private deleteCameraTool() {
    return {
      name: 'delete-camera',
      description:
        'Delete exactly one camera after the user confirms the native confirmation dialog. Use only when the user explicitly asks to delete a camera. Provide cameraName or ipAddress, plus coreName or locationName when needed to make the match unique. Never delete when the target is ambiguous.',
      parameters: z.object({
        cameraName: z.string().optional(),
        ipAddress: z.string().optional(),
        coreName: z.string().optional(),
        locationName: z.string().optional(),
      }),
      handler: async (args: { cameraName?: string; ipAddress?: string; coreName?: string; locationName?: string }) => {
        const [cameras, edges, locations] = await Promise.all([
          firstValueFrom(this.store$.select(CameraSelectors.selectAllCameras).pipe(take(1))),
          firstValueFrom(this.store$.select(EdgeSelectors.selectAllEdges).pipe(take(1))),
          firstValueFrom(this.store$.select(LocationSelectors.selectAllLocations).pipe(take(1))),
        ]);
        const query = (args.cameraName ?? args.ipAddress)?.trim().toLocaleLowerCase();
        const edge = IntelligenceModels.CameraProvisioning.resolveCoreName(edges, args.coreName).edge;
        const location = IntelligenceModels.CameraProvisioning.resolveLocationName(locations, args.locationName).location;
        const matches = cameras.filter((camera) => {
          const cameraId = camera.edgeOnly?.cameraId ?? camera.cameraId;
          const name = camera.edgeOnly?.name ?? camera.name;
          const ip = camera.edgeOnly?.networkDetails?.ipAddress;
          return !!query && [name, ip].some((value) => value?.trim().toLocaleLowerCase() === query) &&
            (!edge || camera.edgeId === edge.edgeId) && (!location || camera.locationId === location._id) && !!cameraId;
        });
        if (matches.length !== 1) return { conflict: true };
        const camera = matches[0];
        const cameraId = camera.edgeOnly?.cameraId ?? camera.cameraId;
        if (!cameraId || !camera.edgeId || !camera.locationId) return { conflict: true };
        const result = await firstValueFrom(this.confirmDialog.open({
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          title: 'Delete camera',
          msg: `Delete camera ${camera.edgeOnly?.name ?? camera.name ?? cameraId}? This cannot be undone.`,
          confirm: 'Delete',
          disableClose: true,
        }));
        if (result.selection !== ConfirmDialogModel.ConfirmResult.OK) return { cancelled: true };
        const completed = firstValueFrom(this.actions$.pipe(ofType(EdgeActions.callDeleteCameraFromLocationSuccess), filter((action) => action.cameraId === cameraId), take(1)));
        this.store$.dispatch(EdgeActions.deleteCameraFromLocation({ edgeId: camera.edgeId, cameraId, locationId: camera.locationId }));
        await completed;
        return { deleted: true, cameraName: camera.edgeOnly?.name ?? camera.name ?? cameraId };
      },
    };
  }

  private deleteCoreTool() {
    return {
      name: 'delete-core',
      description: 'Delete exactly one Core after the user confirms the native confirmation dialog. Use only for an explicit delete request and never when the Core name is ambiguous.',
      parameters: z.object({ coreName: z.string(), locationName: z.string().optional() }),
      handler: async (args: { coreName: string; locationName?: string }) => {
        const [edges, locations] = await Promise.all([
          firstValueFrom(this.store$.select(EdgeSelectors.selectAllEdges).pipe(take(1))),
          firstValueFrom(this.store$.select(LocationSelectors.selectAllLocations).pipe(take(1))),
        ]);
        const location = IntelligenceModels.CameraProvisioning.resolveLocationName(locations, args.locationName).location;
        const matches = edges.filter((edge) => edge.name?.trim().toLocaleLowerCase() === args.coreName.trim().toLocaleLowerCase() && (!location || edge.locationId === location._id));
        if (matches.length !== 1) return { conflict: true };
        const edge = matches[0];
        const result = await firstValueFrom(this.confirmDialog.open({ type: ConfirmDialogModel.ConfirmDialogType.CONFIRM, title: 'Delete Core', msg: `Delete Core ${edge.name}? This cannot be undone.`, confirm: 'Delete', disableClose: true }));
        if (result.selection !== ConfirmDialogModel.ConfirmResult.OK) return { cancelled: true };
        const completed = firstValueFrom(this.actions$.pipe(ofType(EdgeActions.deleteEdgeServerRequestSuccessSocketAndApi), filter((action) => action.edgeId === edge.edgeId), take(1)));
        this.store$.dispatch(EdgeActions.startDeleteEdge({ edgeId: edge.edgeId, locationId: edge.locationId }));
        await completed;
        return { deleted: true, coreName: edge.name };
      },
    };
  }

  private addCoreTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.CoreProvisioning.AddCoreToolParams,
    IntelligenceModels.CoreProvisioning.AddCoreOutcome
  > {
    return {
      name: IntelligenceModels.CoreProvisioning.ToolName.AddCore,
      description:
        'Open the existing Add Core screen. Use when the user asks to add a Core. If the user explicitly gave a Location name, preselect it; otherwise, show the user the existing Location selector. You may provide coreId when the user explicitly gave a Core ID; it is prefilled in the existing Add Core screen. Do not provide a serial number: serial-number lookup is not supported in Cmd+K. If this returns { cancelled: true }, the user dismissed the Location selector: abandon the add-Core flow and acknowledge that no Core was added.',
      parameters: z.object({
        locationName: z
          .string()
          .optional()
          .describe('Location name explicitly stated by the user.'),
        coreId: z
          .string()
          .optional()
          .describe('Core ID explicitly stated by the user; not a serial number.'),
      }),
      handler: async (
        args: IntelligenceModels.CoreProvisioning.AddCoreToolParams,
      ): Promise<IntelligenceModels.CoreProvisioning.AddCoreOutcome> => {
        const locations = await firstValueFrom(
          this.store$.select(LocationSelectors.selectAllLocations).pipe(take(1)),
        );
        let location = IntelligenceModels.CoreProvisioning.resolveAddCoreLocation(
          args.locationName,
          locations,
        );
        if (!location) {
          const selectedLocationIds = await firstValueFrom(
            this.dialog
              .open(LocationPickerModalComponent, {
                panelClass: 'modal-no-padding',
                width: '500px',
                height: '70vh',
                data: { selectedLocationIds: [], maxSelect: 1 },
              })
              .afterClosed(),
          );
          const selectedLocationId = selectedLocationIds?.[0];
          location = locations.find((item) => item._id === selectedLocationId) ?? null;
          if (!location) {
            return { cancelled: true };
          }
        }

        this.locationsService.setAddLocationOptions({
          location,
          edgeId: args.coreId,
          step: ADD_CORE_STEP,
        });
        this.resultsService.setAddCore(
          { locationId: location._id, coreId: args.coreId },
          location.name ?? args.locationName ?? 'Add Core',
        );
        return { opened: true, locationName: location.name ?? args.locationName ?? '' };
      },
    };
  }

  // ── list-walls ──────────────────────────────────────────────────────────

  /**
   * Fetch one page of walls from the intelligence endpoint. Shared by the
   * `list-walls` tool handler and the walls-result component's infinite scroll.
   * `hasMore` is derived client-side: a full page means more may exist.
   */
  public async fetchWalls(
    args: IntelligenceModels.Walls.ListParams,
  ): Promise<IntelligenceModels.Walls.Response> {
    let params = new HttpParams();
    if (args.limit) params = params.set('limit', args.limit);
    if (args.query?.trim()) params = params.set('query', args.query.trim());
    if (args.latestTs) params = params.set('latestTs', args.latestTs);
    // Arrays are sent comma-delimited to match the endpoint's class-transformer DTO.
    if (args.isPrivate?.length)
      params = params.set('isPrivate', args.isPrivate.join(','));
    if (args.tags?.length) params = params.set('tagIds', args.tags.join(','));
    if (args.cameraIdsIncludes?.length)
      params = params.set(
        'cameraIdsIncludes',
        args.cameraIdsIncludes.join(','),
      );
    if (args.cameraIdsExcludes?.length)
      params = params.set(
        'cameraIdsExcludes',
        args.cameraIdsExcludes.join(','),
      );

    const response = await firstValueFrom(
      this.httpClient.get<IntelligenceModels.Walls.Response>(
        api.intelligence.walls,
        { params },
      ),
    );

    return { ...response, hasMore: response.count >= response.filter.limit };
  }

  /** Fetch one page of alerts for the alert preview panel's cursor pagination. */
  public async fetchAlerts(
    args: IntelligenceModels.Alerts.ListParams,
  ): Promise<IntelligenceModels.Alerts.Response> {
    let params = new HttpParams();
    if (args.hoursBack) params = params.set('hoursBack', String(args.hoursBack));
    if (args.startTime) params = params.set('startTime', args.startTime);
    if (args.endTime) params = params.set('endTime', args.endTime);
    if (args.cameraIds?.length) {
      args.cameraIds.forEach((id) => {
        params = params.append('cameraIds', id);
      });
    }
    if (args.cameraNames?.length) {
      args.cameraNames.forEach((name) => {
        params = params.append('cameraNames', name);
      });
    }
    if (args.size !== undefined) params = params.set('size', args.size);
    if (args.latestTs !== undefined)
      params = params.set('latestTs', args.latestTs);
    if (args.eventIds?.length)
      params = params.set('eventIds', args.eventIds.join(','));
    if (args.tagIds?.length) params = params.set('tagIds', args.tagIds.join(','));
    if (args.trackedObjects?.length)
      params = params.set('trackedObjects', args.trackedObjects.join(','));
    if (args.acknowledges?.length)
      params = params.set('acknowledges', args.acknowledges.join(','));
    if (args.flowTypes?.length)
      params = params.set('flowTypes', JSON.stringify(args.flowTypes));
    if (args.alertTypes?.length)
      params = params.set('alertTypes', JSON.stringify(args.alertTypes));

    return firstValueFrom(
      this.httpClient.get<IntelligenceModels.Alerts.Response>(
        api.intelligence.alerts,
        { params },
      ),
    );
  }

  /** Delete a wall by id (used by the wall-result row menu). */
  public deleteWall(wallId: string): Observable<unknown> {
    return this.httpClient.delete(`${api.wallV2.crud}/${wallId}`);
  }

  /** Create a wall (used by the wall-result "Create wall" quick flow). */
  public createWall(
    dto: WallV2Model.WallCreateDto,
  ): Observable<WallV2Model.WallMongoDocument> {
    return this.httpClient.post<WallV2Model.WallMongoDocument>(
      api.wallV2.crud,
      dto,
    );
  }

  /** Update a wall by id (used by the Cmd+K wall editor). */
  public updateWall(wall: WallV2Model.WallMongoDocument): Observable<unknown> {
    return this.httpClient.put(`${api.wallV2.crud}/${wall._id}`, wall);
  }

  /** Persist the Cmd+K AI wall draft, then return a refreshed wall list. */
  public async createWallFromDraft(
    dto: WallV2Model.WallCreateDto,
  ): Promise<IntelligenceModels.Walls.Response> {
    await firstValueFrom(this.createWall(dto));
    return this.fetchWalls({ limit: IntelligenceModels.Walls.DEFAULT_LIMIT });
  }

  /** Persist the Cmd+K wall edit draft, then return a refreshed wall list. */
  public async updateWallFromDraft(
    wall: WallV2Model.WallMongoDocument,
  ): Promise<IntelligenceModels.Walls.Response> {
    await firstValueFrom(this.updateWall(wall));
    return this.fetchWalls({ limit: IntelligenceModels.Walls.DEFAULT_LIMIT });
  }

  private wallsTool() {
    return {
      name: 'list-walls',
      description:
        "Fetch and display the organization's video walls (saved multi-camera layouts) in the preview panel. " +
        'Use for browsing, listing, searching, or filtering walls. ' +
        'When the user asks to show, view, or open one specific wall by name, use view-wall instead. ' +
        'Supports a wall name query, a result limit, and a latestTs cursor for pagination. ' +
        'After calling this tool ALWAYS write a short text reply summarising what was found.',
      parameters: z.object({
        limit: z
          .number()
          .int()
          .positive()
          .optional()
          .describe('Maximum number of walls to return. Default 100.'),
        query: z.string().optional().describe('Wall name search query.'),
        latestTs: z
          .number()
          .positive()
          .optional()
          .describe('Return walls created before this timestamp in ms.'),
      }),
      handler: async (args: {
        limit?: number;
        query?: string;
        latestTs?: number;
      }) => {
        const response = await this.fetchWalls(args);
        const label = `${response.count} walls${response.filter.query ? ` · ${response.filter.query}` : ''}`;
        this.resultsService.setWalls(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          filter: response.filter,
          message: response.message,
        };
      },
    };
  }

  private viewWallTool() {
    return {
      name: 'view-wall',
      description:
        'Open one specific video wall by name in the preview panel camera-detail view. ' +
        'Use when the user asks to show, view, or open a wall, its cameras, layout, or template by name. ' +
        'The name match is case-insensitive and may be partial. If it does not resolve to one wall, ' +
        'the matching walls list is displayed instead. Prefer list-walls when the user wants to browse or filter walls. ' +
        'After calling this tool ALWAYS write a short text reply summarising what was opened or found.',
      parameters: z.object({
        name: z
          .string()
          .describe(
            'The wall name to look up (case-insensitive; partial match supported).',
          ),
      }),
      handler: async (args: IntelligenceModels.Walls.ViewWallParams) => {
        const name = args.name.trim();
        const candidates = await this.fetchWalls({
          limit: IntelligenceModels.Walls.DEFAULT_LIMIT,
          query: name,
        });
        const normalizedName = name.toLowerCase();
        const exact = candidates.walls.find(
          (wall) => wall.name.trim().toLowerCase() === normalizedName,
        );
        const resolved =
          exact ?? (candidates.walls.length === 1 ? candidates.walls[0] : null);

        if (resolved) {
          this.resultsService.setWallDetail(
            { _id: resolved._id, name: resolved.name },
            resolved.name,
          );
          return {
            panelUpdated: true,
            wallFound: true,
            wallId: resolved._id,
            name: resolved.name,
            message: `Wall "${resolved.name}" displayed in the preview panel.`,
          };
        }

        const response = candidates.walls.length
          ? candidates
          : await this.fetchWalls({
              limit: IntelligenceModels.Walls.DEFAULT_LIMIT,
            });
        const label = `${response.count} walls${candidates.walls.length ? ` · ${name}` : ''}`;
        this.resultsService.setWalls(response, label);
        return {
          panelUpdated: true,
          wallFound: false,
          count: response.count,
          filter: response.filter,
          message: candidates.walls.length
            ? `Found ${candidates.walls.length} walls matching "${name}". Showing the matching walls list.`
            : `No wall matching "${name}" was found. Showing all walls.`,
        };
      },
    };
  }

  private prepareCreateWallTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.Walls.Create.Params,
    IntelligenceModels.Walls.Create.PrepareResponse
  > {
    return {
      name: IntelligenceModels.Walls.Create.ToolName.Prepare,
      description: IntelligenceModels.Walls.Create.PREPARE_CREATE_TOOL_DESCRIPTION,
      parameters: z.object({
        name: z.string().optional().describe('Wall name supplied by the user.'),
        cameraIds: z
          .array(z.string())
          .optional()
          .describe(
            'Exact camera IDs returned by pick-cameras. Pass those IDs verbatim without resolving or changing them.',
          ),
        cameraNames: z
          .array(z.string())
          .optional()
          .describe(
            'Camera-name hints resolved server-side. Do not call discovery tools to resolve these names first.',
          ),
        noCameras: z
          .boolean()
          .optional()
          .describe('Set true only when the user explicitly chooses to create an empty wall.'),
      }),
      handler: async (
        args: IntelligenceModels.Walls.Create.Params,
      ): Promise<IntelligenceModels.Walls.Create.PrepareResponse> => {
        try {
          const result = await firstValueFrom(
            this.httpClient.post<IntelligenceModels.Walls.Create.PrepareResponse>(
              api.intelligence.wallsPrepareCreate,
              args,
            ),
          );
          if (result.prepared) this.stashPreparedWallDraft(result.prepared);
          return result;
        } catch (error: unknown) {
          return {
            error: this.buildWallToolError(error, [
              'Call prepare-create-wall again with the values the user supplied.',
            ]),
          };
        }
      },
    };
  }

  private createWallTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.Walls.Create.ExecuteParams,
    IntelligenceModels.Walls.Create.ToolResponse
  > {
    return {
      name: IntelligenceModels.Walls.Create.ToolName.Create,
      description: IntelligenceModels.Walls.Create.CREATE_TOOL_DESCRIPTION,
      parameters: z.object({
        preparation: z
          .string()
          .min(1)
          .describe('Preparation token returned by prepare-create-wall.'),
      }),
      handler: async (
        args: IntelligenceModels.Walls.Create.ExecuteParams,
      ): Promise<IntelligenceModels.Walls.Create.ToolResponse> => {
        let result: IntelligenceModels.Walls.Create.ExecuteResponse;
        try {
          result = await firstValueFrom(
            this.httpClient.post<IntelligenceModels.Walls.Create.ExecuteResponse>(
              api.intelligence.wallsCreate,
              args,
            ),
          );
        } catch (error: unknown) {
          return {
            error: this.buildWallToolError(error, [
              'Check the walls list before retrying, in case the wall was created.',
              'If the preparation token expired, call prepare-create-wall again to get a fresh one.',
            ]),
          };
        }

        if (result.error) return { error: result.error };
        if (
          !result.wallCreated ||
          !result.walls ||
          typeof result.count !== 'number' ||
          !result.filter ||
          typeof result.message !== 'string' ||
          typeof result.wallId !== 'string' ||
          typeof result.name !== 'string' ||
          typeof result.tilesCount !== 'number'
        ) {
          return {
            error: {
              code: IntelligenceModels.Walls.Create.ErrorCode.Upstream,
              message: 'The wall service returned an incomplete creation response.',
              recovery: ['Check the walls list before trying again.'],
            },
          };
        }

        const response: IntelligenceModels.Walls.Response = {
          walls: result.walls,
          count: result.count,
          filter: result.filter,
          message: result.message,
        };
        this.resultsService.setWalls(response, `${response.count} walls`);
        return {
          panelUpdated: true,
          wallCreated: true,
          wallId: result.wallId,
          name: result.name,
          tilesCount: result.tilesCount,
          message: result.message,
          assistant: result.assistant,
        };
      },
    };
  }

  private reviewWallDraftTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.Walls.Create.ExecuteParams,
    IntelligenceModels.Walls.Create.ToolResponse
  > {
    return {
      name: IntelligenceModels.Walls.Create.ToolName.Review,
      description: IntelligenceModels.Walls.Create.REVIEW_TOOL_DESCRIPTION,
      parameters: z.object({
        preparation: z
          .string()
          .min(1)
          .describe('Preparation token returned by prepare-create-wall.'),
      }),
      handler: async (
        args: IntelligenceModels.Walls.Create.ExecuteParams,
      ): Promise<IntelligenceModels.Walls.Create.ToolResponse> => {
        const draft =
          this.preparedWallDrafts.get(args.preparation) ??
          this.decodePreparedWallDraft(args.preparation);
        if (!draft) {
          return {
            error: {
              code: IntelligenceModels.Walls.Create.ErrorCode.InvalidPreparation,
              message: 'This wall preparation is invalid or cannot be read.',
              recovery: ['Call prepare-create-wall again.'],
            },
          };
        }

        this.resultsService.setCreateWallDraft(
          { name: draft.name, cameraIds: draft.cameraIds },
          draft.name || 'Create wall',
        );
        return {
          panelUpdated: true,
          draftOpened: true,
          message: 'Wall draft open for review; the user must press Save to create it.',
        };
      },
    };
  }

  private buildWallToolError(
    error: unknown,
    recovery: string[],
  ): IntelligenceModels.Walls.Create.Error {
    return {
      code: IntelligenceModels.Walls.Create.ErrorCode.Upstream,
      message:
        error instanceof Error ? error.message : 'Could not reach the wall service.',
      recovery,
    };
  }

  private stashPreparedWallDraft(
    prepared: IntelligenceModels.Walls.Create.Prepared,
  ): void {
    if (this.preparedWallDrafts.size >= PREPARED_WALL_DRAFTS_MAX_SIZE) {
      const oldestPreparation = this.preparedWallDrafts.keys().next().value;
      if (oldestPreparation) this.preparedWallDrafts.delete(oldestPreparation);
    }
    this.preparedWallDrafts.set(prepared.preparation, prepared.draft);
  }

  private decodePreparedWallDraft(
    preparation: string,
  ): IntelligenceModels.Walls.Create.PreparedDraft | null {
    try {
      const base64 = preparation.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
      const bytes = Uint8Array.from(decoded, (character: string) => character.charCodeAt(0));
      const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
      const preparationPayload = payload as IntelligenceModels.Walls.Create.PreparationPayload;
      if (
        typeof preparationPayload.name !== 'string' ||
        !Array.isArray(preparationPayload.cameraIds) ||
        !preparationPayload.cameraIds.every(
          (cameraId: unknown): cameraId is string => typeof cameraId === 'string',
        )
      ) {
        return null;
      }
      return {
        name: preparationPayload.name,
        cameraIds: preparationPayload.cameraIds,
      };
    } catch {
      return null;
    }
  }

  // ── organization-members ──────────────────────────────────────────────────

  private organizationMembersTool() {
    return {
      name: 'organization-members',
      description:
        'Fetch and display organization members in the preview panel. ' +
        'ALWAYS call this tool for ANY user request about members — initial queries AND follow-up questions. ' +
        'For requests to show a named user, pass their name in "filter". Use "filter" to INCLUDE members (OR logic across comma-separated terms). ' +
        'Use "exclude" to REMOVE members that match those terms (AND NOT logic). ' +
        'When a user asks for members with a specific permission, ALWAYS pass that permission-group name in "permissionGroup", never in "filter". ' +
        'After calling this tool ALWAYS write a short text reply (1-2 sentences) summarising what was found.',
      parameters: z.object({
        filter: z
          .string()
          .optional()
          .describe(
            'Comma-separated OR inclusion terms matched against name and email.',
          ),
        exclude: z
          .string()
          .optional()
          .describe('Comma-separated AND NOT exclusion terms.'),
        permissionGroup: z
          .string()
          .optional()
          .describe(
            'Exact permission-group name to apply in the panel selector, case-insensitive. Use for prompts such as "Show me all Admin users".',
          ),
      }),
      handler: async ({
        filter,
        exclude,
        permissionGroup,
      }: {
        filter?: string;
        exclude?: string;
        permissionGroup?: string;
      }) => {
        const requestedPermissionGroup = permissionGroup?.trim();
        let params = new HttpParams();
        if (filter?.trim()) params = params.set('filter', filter.trim());
        if (exclude?.trim()) params = params.set('exclude', exclude.trim());
        if (requestedPermissionGroup) {
          params = params.set('permissionGroup', requestedPermissionGroup);
        }
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Organizations.MembersResponse>(
            api.intelligence.organizationMembers,
            { params },
          ),
        );
        const label = `${response.count}${response.filter ? ` · ${response.filter}` : ''}${response.exclude ? ` not ${response.exclude}` : ''}${requestedPermissionGroup ? ` · ${requestedPermissionGroup}` : ''}`;
        this.resultsService.setOrganizationMembers(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          filter: response.filter,
          exclude: response.exclude,
          selectedPermissionGroupIds: response.selectedPermissionGroupIds,
          message: response.message,
        };
      },
    };
  }

  // ── get-device-health ─────────────────────────────────────────────────────

  private deviceHealthTool() {
    return {
      name: 'get-device-health',
      description:
        'Fetch and display the health status of the organization cameras and Cores as a flat list. ' +
        'Use for any question about which cameras or Cores are online, offline or unhealthy — ' +
        'for example "which cameras are offline at <location>", "is <camera name> online", ' +
        '"how many Cores are unhealthy". All filters are optional and applied server-side. ' +
        'After calling this tool ALWAYS write a short text reply summarising the statuses. ' +
        'Set every explicit filter in the request; “with issues” means the Devices with issues advanced filter, ' +
        'not an inferred overall status.',
      parameters: z.object({
        location: z
          .string()
          .optional()
          .describe('Location id, or part of a location name, to restrict the list to.'),
        type: z
          .enum([
            IntelligenceModels.DeviceList.DeviceType.Camera,
            IntelligenceModels.DeviceList.DeviceType.Core,
          ])
          .optional()
          .describe('Restrict the list to cameras or to Cores.'),
        status: z
          .enum([
            IntelligenceModels.HealthStatus.Online,
            IntelligenceModels.HealthStatus.Unhealthy,
            IntelligenceModels.HealthStatus.Offline,
            IntelligenceModels.HealthStatus.Installing,
          ])
          .optional()
          .describe(
            'One explicit overall health status. Use the literal value named by the user; ' +
              'do not infer Offline or Unhealthy from “with issues”.',
          ),
        statuses: z
          .array(
            z.enum([
              IntelligenceModels.HealthStatus.Online,
              IntelligenceModels.HealthStatus.Unhealthy,
              IntelligenceModels.HealthStatus.Offline,
              IntelligenceModels.HealthStatus.Installing,
            ]),
          )
          .optional()
          .describe('All explicitly requested overall statuses, e.g. ["unhealthy", "offline"].'),
        advanced: z
          .array(z.enum(Object.values(IntelligenceModels.DeviceHealth.AdvancedFilter) as [string, ...string[]]))
          .optional()
          .describe(
            'Advanced health conditions. Map user wording exactly: “devices with issues”/“with issues” → ' +
              'devicesWithIssues; “unhealthy analytic” → unhealthyAnalytic; “unhealthy substream” → ' +
              'unhealthySubstream; “unhealthy playback” → unhealthyPlayback; “no customized model” → ' +
              'noCustomizedModel; “no substream” → noSubstream. Include every condition explicitly requested.',
          ),
        name: z.string().optional().describe('Part of the device name to restrict the list to.'),
      }),
      handler: async (args: IntelligenceModels.DeviceHealth.ToolParams) => {
        // Array-shaped params: every list filter is multi-value now. A prompt
        // still names one value, so each lands in a one-element array.
        let params = new HttpParams();
        if (args?.location) params = params.append('locations', args.location);
        if (args?.type) params = params.append('types', args.type);
        const statuses = [...(args?.statuses ?? []), ...(args?.status ? [args.status] : [])];
        for (const status of new Set(statuses)) params = params.append('statuses', status);
        for (const advanced of new Set(args?.advanced ?? [])) params = params.append('advanced', advanced);
        if (args?.name) params = params.set('name', args.name);

        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.DeviceHealth.Response>(
            api.intelligence.deviceHealth,
            { params },
          ),
        );
        const label = `${response.count} devices · ${response.counts.online} online`;
        this.resultsService.setDeviceHealth(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          counts: response.counts,
          message: response.message,
        };
      },
    };
  }

  // ── get-device-list ───────────────────────────────────────────────────────

  private deviceListTool() {
    return {
      name: 'get-device-list',
      description:
        'Fetch and display the complete organization device inventory grouped by location. ' +
        'Use when the user asks for all devices, the device list, device inventory, empty locations, Cores, cameras, Apple TVs, doors, speakers, sensors, Remote IO, external storage, or access-control devices. ' +
        'Do not use get-locations for a full inventory and do not use edge health unless the question is specifically about health. ' +
        'After calling this tool write a short reply summarising the location and device counts.',
      parameters: z.object({}),
      handler: async () => {
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.DeviceList.Response>(
            api.intelligence.deviceList,
          ),
        );
        this.resultsService.setDeviceList(
          response,
          `${response.locationCount} locations · ${response.deviceCount} devices`,
        );
        return {
          panelUpdated: true,
          locationCount: response.locationCount,
          deviceCount: response.deviceCount,
          message: response.message,
        };
      },
    };
  }

  // ── get-locations ─────────────────────────────────────────────────────────

  private locationsTool() {
    return {
      name: 'get-locations',
      description:
        'Fetch and display all organization locations in the preview panel. ' +
        'Use for any question about sites, branches, offices, locations, where cameras are deployed, or how many locations the org has. ' +
        'No parameters required. After calling this tool write a short reply summarising the locations count.',
      parameters: z.object({}),
      handler: async () => {
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Locations.Response>(
            api.intelligence.locations,
          ),
        );
        this.resultsService.setLocations(
          response,
          `${response.count} locations`,
        );
        return {
          panelUpdated: true,
          count: response.count,
          message: response.message,
        };
      },
    };
  }

  // ── get-persons ───────────────────────────────────────────────────────────

  private personsTool() {
    return {
      name: 'get-persons',
      description:
        'Fetch and display registered persons (watchlist) in the preview panel. ' +
        'Use for any question about known persons, watchlist, faces, or named individuals. ' +
        'Response includes a persons array with personId, name, appearances, and lastSeen — use personId to call simple-search-person for camera footage of a specific person. ' +
        'After calling this tool write a short reply summarising the count.',
      parameters: z.object({
        showHidden: z
          .boolean()
          .optional()
          .describe('If true, show only hidden persons.'),
      }),
      handler: async ({ showHidden }: { showHidden?: boolean }) => {
        let params = new HttpParams();
        if (showHidden) params = params.set('showHidden', 'true');
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Persons.Response>(
            api.intelligence.persons,
            { params },
          ),
        );
        this.resultsService.setPersons(response, `${response.count} persons`);
        return {
          panelUpdated: true,
          count: response.count,
          message: response.message,
          persons: response.persons?.map((p, i) => ({
            index: i,
            personId: p.personId,
            name: p.name,
            appearances: p.appearances,
            lastSeen: p.lastSeen,
          })),
        };
      },
    };
  }

  // ── get-person-groups ─────────────────────────────────────────────────────

  private personGroupsTool() {
    return {
      name: 'get-person-groups',
      description:
        'Fetch and display person groups in the preview panel. ' +
        'Use for any question about person groups, watchlist groups, shared groups, or which groups exist. ' +
        'Use "query" to filter by group name. ' +
        'Response includes a groups array with _id, name, and personsCounter — use _id to call get-person-group-members for the persons inside a group. ' +
        'After calling this tool write a short reply summarising the count.',
      parameters: z.object({
        query: z
          .string()
          .optional()
          .describe('Case-insensitive group name search query.'),
        limit: z
          .number()
          .optional()
          .describe('Maximum number of groups to return (default 50).'),
      }),
      handler: async ({ query, limit }: { query?: string; limit?: number }) => {
        let params = new HttpParams();
        if (query?.trim()) params = params.set('query', query.trim());
        if (limit) params = params.set('limit', String(limit));
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.PersonGroups.Response>(
            api.intelligence.personGroups,
            { params },
          ),
        );
        this.resultsService.setPersonGroups(
          response,
          `${response.count} person groups`,
        );
        return {
          panelUpdated: true,
          count: response.count,
          message: response.message,
          groups: response.groups?.map((g, i) => ({
            index: i,
            _id: g._id,
            name: g.name,
            personsCounter: g.personsCounter,
            accessListCounter: g.accessListCounter,
          })),
        };
      },
    };
  }

  // ── get-person-group-members ──────────────────────────────────────────────

  private personGroupMembersTool() {
    return {
      name: 'get-person-group-members',
      description:
        'Fetch and display the persons that belong to a person group in the preview panel. ' +
        'Use when the user asks who is in a group — read the group _id from get-person-groups results (NEVER invent it). ' +
        'Response includes a persons array with personId and name — use personId to call simple-search-person for camera footage. ' +
        'After calling this tool write a short reply summarising the member count.',
      parameters: z.object({
        groupId: z
          .string()
          .describe('The person group _id from get-person-groups.'),
        limit: z
          .number()
          .optional()
          .describe('Maximum number of members to return (default 50).'),
      }),
      handler: async ({
        groupId,
        limit,
      }: {
        groupId: string;
        limit?: number;
      }) => {
        let params = new HttpParams();
        if (limit) params = params.set('limit', String(limit));
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.PersonGroups.MembersResponse>(
            `${api.intelligence.personGroups}/${groupId}/persons`,
            { params },
          ),
        );
        const label = `${response.count} of ${response.totalCount} · ${response.groupName}`;
        this.resultsService.setPersonGroups(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          totalCount: response.totalCount,
          groupName: response.groupName,
          message: response.message,
          persons: response.persons?.map((p, i) => ({
            index: i,
            personId: p.personId,
            name: p.name,
          })),
        };
      },
    };
  }

  // ── get-dashboards ────────────────────────────────────────────────────────

  private dashboardsTool() {
    return {
      name: 'get-dashboards',
      description:
        'Fetch and display the analytics dashboards (the ones on the Dashboards page) in the preview panel. ' +
        'Use for any question about existing dashboards or to list them. ' +
        'Response includes a dashboards array with id, name, and last-updated time. ' +
        'After calling this tool write a short reply summarising the count.',
      parameters: z.object({}),
      handler: async () => {
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Dashboards.Response>(
            api.intelligence.dashboards,
          ),
        );
        this.resultsService.setDashboards(
          response,
          `${response.count} dashboards`,
        );
        return {
          panelUpdated: true,
          count: response.count,
          message: response.message,
          dashboards: response.dashboards?.map((d, i) => ({
            index: i,
            id: d.id,
            name: d.name,
            updatedAt: d.updatedAt,
          })),
        };
      },
    };
  }

  // ── get-vehicles ──────────────────────────────────────────────────────────

  private vehiclesTool() {
    return {
      name: 'get-vehicles',
      description:
        'Fetch and display registered vehicles (license-plate watchlist) in the preview panel. ' +
        'Use for any question about known vehicles, watchlist plates, or registered cars. ' +
        'Response includes a vehicles array with id, plate, owner, make, and color. ' +
        'After calling this tool write a short reply summarising the count.',
      parameters: z.object({}),
      handler: async () => {
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Vehicles.Response>(
            api.intelligence.vehicles,
          ),
        );
        this.resultsService.setVehicles(response, `${response.count} vehicles`);
        return {
          panelUpdated: true,
          count: response.count,
          message: response.message,
          vehicles: response.vehicles?.map((v, i) => ({
            index: i,
            id: v.id,
            plate: v.plate,
            owner: v.owner,
            make: v.make,
            color: v.color,
          })),
        };
      },
    };
  }

  // ── get-vehicle-groups ────────────────────────────────────────────────────

  private vehicleGroupsTool() {
    return {
      name: 'get-vehicle-groups',
      description:
        'Fetch and display vehicle groups in the preview panel. ' +
        'Use for any question about vehicle groups, plate groups, shared groups, or which vehicle groups exist. ' +
        'Use "query" to filter by group name. ' +
        'Response includes a groups array with _id, name, and vehiclesCounter — use _id to call get-vehicle-group-members for the vehicles inside a group. ' +
        'After calling this tool write a short reply summarising the count.',
      parameters: z.object({
        query: z
          .string()
          .optional()
          .describe('Case-insensitive group name search query.'),
        limit: z
          .number()
          .optional()
          .describe('Maximum number of groups to return (default 50).'),
      }),
      handler: async ({ query, limit }: { query?: string; limit?: number }) => {
        let params = new HttpParams();
        if (query?.trim()) params = params.set('query', query.trim());
        if (limit) params = params.set('limit', String(limit));
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.VehicleGroups.Response>(
            api.intelligence.vehicleGroups,
            { params },
          ),
        );
        this.resultsService.setVehicleGroups(
          response,
          `${response.count} vehicle groups`,
        );
        return {
          panelUpdated: true,
          count: response.count,
          message: response.message,
          groups: response.groups?.map((g, i) => ({
            index: i,
            _id: g._id,
            name: g.name,
            vehiclesCounter: g.vehiclesCounter,
            accessListCounter: g.accessListCounter,
          })),
        };
      },
    };
  }

  // ── get-vehicle-group-members ─────────────────────────────────────────────

  private vehicleGroupMembersTool() {
    return {
      name: 'get-vehicle-group-members',
      description:
        'Fetch and display the vehicles that belong to a vehicle group in the preview panel. ' +
        'Use when the user asks which vehicles are in a group — read the group _id from get-vehicle-groups results (NEVER invent it). ' +
        'Response includes a vehicles array with id and plate. ' +
        'After calling this tool write a short reply summarising the vehicle count.',
      parameters: z.object({
        groupId: z
          .string()
          .describe('The vehicle group _id from get-vehicle-groups.'),
        limit: z
          .number()
          .optional()
          .describe('Maximum number of vehicles to return (default 50).'),
      }),
      handler: async ({
        groupId,
        limit,
      }: {
        groupId: string;
        limit?: number;
      }) => {
        let params = new HttpParams();
        if (limit) params = params.set('limit', String(limit));
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.VehicleGroups.MembersResponse>(
            `${api.intelligence.vehicleGroups}/${groupId}/vehicles`,
            { params },
          ),
        );
        const label = `${response.count} of ${response.totalCount} · ${response.groupName}`;
        this.resultsService.setVehicleGroups(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          totalCount: response.totalCount,
          groupName: response.groupName,
          message: response.message,
          vehicles: response.vehicles?.map((v, i) => ({
            index: i,
            id: v.id,
            plate: v.plate,
          })),
        };
      },
    };
  }

  // ── get-variables ─────────────────────────────────────────────────────────

  private variablesTool() {
    return {
      name: 'get-variables',
      description:
        'Fetch organization configuration variables. ' +
        'Use for questions about configuration, settings, variables, or thresholds. ' +
        'Opens a preview panel listing each variable name, type and value, ' +
        'and also returns the values in the chat.',
      parameters: z.object({
        name: z
          .string()
          .optional()
          .describe(
            'Specific variable name to retrieve. Omit for all variables.',
          ),
      }),
      handler: async ({ name }: { name?: string }) => {
        const requestedName = name?.trim();
        let params = new HttpParams();
        if (requestedName) params = params.set('name', requestedName);
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Variables.Response>(
            api.intelligence.variables,
            { params },
          ),
        );
        this.store$.dispatch(
          OrgVariablesActions.getOrgVariablesSuccess({
            variables: response.variables as OrgVariablesModel.Variable[],
          }),
        );
        this.store$.dispatch(OrgVariablesActions.setIsLoading({ isLoading: false }));
        const label = `${response.count} variables${requestedName ? ` · ${requestedName}` : ''}`;
        this.resultsService.setVariables(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          variables: response.variables,
          message: response.message,
        };
      },
    };
  }

  // ── get-current-user-info ─────────────────────────────────────────────────

  private currentUserInfoTool() {
    return {
      name: 'get-current-user-info',
      description:
        "Fetch the current user's own profile: id, full name, email, phone and language. " +
        'Use for "who is my user?", "show me info about my user", "what is my email/phone/language?". ' +
        'Returns the answer in the chat (no dedicated preview panel). ' +
        "After calling this tool ALWAYS write a short text reply summarising the user's details, " +
        'showing the user id next to their name. ' +
        'To look up other people in the organization use organization-members instead.',
      parameters: z.object({}),
      handler: async () => {
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Users.InfoResponse>(
            api.intelligence.currentUser,
          ),
        );
        return {
          id: response.id,
          fullName: response.fullName,
          firstname: response.firstname,
          lastname: response.lastname,
          email: response.email,
          phone: response.phone,
          language: response.language,
          languageSource: response.languageSource,
          message: response.message,
        };
      },
    };
  }

  // ── get-organization-info ─────────────────────────────────────────────────

  private organizationInfoTool() {
    return {
      name: 'get-organization-info',
      description:
        "Fetch the current user's organization id and name. " +
        'Use for "what is my organisation?", "show info about my organization", "what is my org id?". ' +
        'Returns the answer in the chat (no dedicated preview panel). ' +
        'After calling this tool ALWAYS write a short text reply naming the organization, ' +
        'showing its id next to its name. ' +
        "NEVER infer the organization from the user's email domain — always call this tool. " +
        'For all organizations available to the user, call user-organisations instead.',
      parameters: z.object({}),
      handler: async () => {
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Organizations.InfoResponse>(
            api.intelligence.organizations,
          ),
        );
        return { orgId: response.orgId, orgName: response.orgName };
      },
    };
  }

  // ── user-organisations ────────────────────────────────────────────────────

  private userOrganizationsTool() {
    return {
      name: 'user-organisations',
      description:
        'Fetch every organization available to the current user and show them in the results panel table. ' +
        'Use for requests such as "show me all my organisations" or "which organizations can I access?". ' +
        'The panel table shows the organization rows — NEVER enumerate organization names or ids in the reply. ' +
        'Reply with one concise sentence stating the total and, when present, the active organization in bold, ' +
        'e.g. "You have 4 available organizations. Active: **DVIR ORG**." ' +
        'When activeOrganizationName is null, omit the active part. ' +
        'If the list is empty, say that no organizations are available.',
      parameters: z.object({}),
      handler: async () => {
        const organizations = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Organizations.UserOrganizationsResponse>(
            api.intelligence.userOrganisations,
          ),
        );
        const activeOrgId = this.activeOrganization()?.orgId;
        const activeOrganization =
          organizations.find((organization) => organization.id === activeOrgId) ?? null;
        const view: IntelligenceModels.Organizations.UserOrganizationsViewResponse = {
          organizations,
          count: organizations.length,
          activeOrganization,
        };
        const label = `${view.count} organizations${activeOrganization ? ` · ${activeOrganization.name}` : ''}`;
        this.resultsService.setUserOrganizations(view, label);
        return {
          panelUpdated: true,
          count: view.count,
          activeOrganizationName: activeOrganization?.name ?? null,
        };
      },
    };
  }

  // ── list-tags ─────────────────────────────────────────────────────────────

  private listTagsTool() {
    return {
      name: 'list-tags',
      description:
        'Fetch every organization tag and show them in the results panel table. ' +
        'Use for requests such as "show me all tags", "list our tags" or "which tags do we have?". ' +
        'Tags are shared by cases, walls, cameras and the other taggable resources. ' +
        'The panel table shows the tag rows — NEVER enumerate tag names in the reply. ' +
        'Reply with one concise sentence stating the total, e.g. "You have 12 tags." ' +
        'If the list is empty, say that no tags exist yet.',
      parameters: z.object({}),
      handler: async () => {
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Tags.ListResponse>(api.intelligence.tags),
        );
        this.resultsService.setTags(response, `${response.count} tags`);
        return { panelUpdated: true, count: response.count };
      },
    };
  }

  // ── list-event-tags ───────────────────────────────────────────────────────

  /**
   * Fetch the organization's event tags. Shared by the `list-event-tags` tool
   * handler and by the create-event-tag panel, which re-fetches after a save
   * because `addCustomEventSuccess` echoes the request and so never carries the
   * server-assigned id.
   */
  public async fetchEventTags(): Promise<IntelligenceModels.EventTags.ListResponse> {
    return firstValueFrom(
      this.httpClient.get<IntelligenceModels.EventTags.ListResponse>(api.intelligence.eventTags),
    );
  }

  /**
   * Hydrate the WHOLE custom-event document behind a listed event tag.
   *
   * `list-event-tags` returns only `_id`/`name` — the editor needs the video
   * length and the field rows too, and deleting needs the document the
   * `removeCustomEvent` action carries. Reading the same collection the Database
   * page's store reads keeps the two surfaces on one document shape.
   *
   * Resolves to null when the id is gone (deleted from another surface since the
   * listing), which callers report rather than acting on nothing.
   */
  public async fetchEventTag(eventTagId: string): Promise<CustomEventModel.CustomEvent | null> {
    const { customEvents } = await firstValueFrom(
      this.httpClient.get<CustomEventModel.GetCustomEventsResponse>(api.customEvents.crud),
    );

    return customEvents.find((customEvent) => customEvent._id === eventTagId) ?? null;
  }

  private listEventTagsTool() {
    return {
      name: 'list-event-tags',
      description:
        'Fetch every organization event tag and show them in the results panel table. ' +
        'Event tags are the custom event types the Database → Event tags page defines, ' +
        'which manual and integration events are recorded against. ' +
        'Use for requests such as "show me our event tags", "list the event types" or ' +
        '"which event tags do we have?". ' +
        'The panel table shows the event tag rows — NEVER enumerate event tag names in the reply. ' +
        'Reply with one concise sentence stating the total, e.g. "You have 4 event tags." ' +
        'If the list is empty, say that no event tags exist yet.',
      parameters: z.object({}),
      handler: async () => {
        const response = await this.fetchEventTags();
        this.resultsService.setEventTags(response, `${response.count} event tags`);
        return { panelUpdated: true, count: response.count };
      },
    };
  }

  // ── list-time-schedules ───────────────────────────────────────────────────

  // ── list-case-categories ──────────────────────────────────────────────────

  /**
   * Fetch the organization's case categories — the retention buckets the
   * Database → Categories page defines. Read-only: the panel draws the page's
   * Create button and row icons for parity but wires none of them, and there is
   * no write route for categories on the intelligence host.
   */
  public async fetchCaseCategories(): Promise<IntelligenceModels.CaseManager.CategoriesResponse> {
    return firstValueFrom(
      this.httpClient.get<IntelligenceModels.CaseManager.CategoriesResponse>(
        api.intelligence.caseManagerCategories,
      ),
    );
  }

  private listCaseCategoriesTool() {
    return {
      name: 'list-case-categories',
      description:
        'Fetch every organization case category and show them in the results panel table. ' +
        'Case categories are the named retention buckets the Database → Categories page defines, ' +
        'which every case is filed under and which decide how long that case is kept. ' +
        'Use for requests such as "show me our case categories", "list the categories" or ' +
        '"what retention periods do we have?". ' +
        'These are NOT alert categories: never use this tool for questions about alert types, ' +
        'alert templates or event categories. ' +
        'The panel table shows the category rows — NEVER enumerate category names in the reply. ' +
        'Reply with one concise sentence stating the total, e.g. "You have 4 case categories." ' +
        'If the list is empty, say that no case categories exist yet.',
      parameters: z.object({}),
      handler: async () => {
        const response = await this.fetchCaseCategories();
        this.resultsService.setCaseManagerCategories(response, `${response.count} case categories`);
        return { panelUpdated: true, count: response.count };
      },
    };
  }

  /**
   * Fetch the organization's time schedules — the named weekly schedules the
   * Database → Time schedule page defines. Read-only: the panel shows the names
   * and nothing here creates, edits, or deletes one.
   */
  public async fetchTimeSchedules(): Promise<IntelligenceModels.TimeSchedules.ListResponse> {
    return firstValueFrom(
      this.httpClient.get<IntelligenceModels.TimeSchedules.ListResponse>(api.intelligence.timeSchedule),
    );
  }

  private listTimeSchedulesTool() {
    return {
      name: 'list-time-schedules',
      description:
        'Fetch every organization time schedule and show them in the results panel table. ' +
        'Time schedules are the named weekly schedules the Database → Time schedule page defines, ' +
        'which alerts and other features are restricted to. ' +
        'Use for requests such as "show me our time schedules", "list the schedules" or ' +
        '"which schedules do we have?". ' +
        'The panel table shows the schedule rows — NEVER enumerate schedule names in the reply. ' +
        'Reply with one concise sentence stating the total, e.g. "You have 4 time schedules." ' +
        'If the list is empty, say that no time schedules exist yet. ' +
        'Do not use this tool for creation requests — use create-time-schedule instead.',
      parameters: z.object({}),
      handler: async () => {
        const response = await this.fetchTimeSchedules();
        this.resultsService.setTimeSchedules(response, `${response.count} time schedules`);
        return { panelUpdated: true, count: response.count };
      },
    };
  }

  // ── create-time-schedule ──────────────────────────────────────────────────

  /**
   * Open the time schedule editor, seeded with the name the user asked for and
   * the page's own default week. The description is the only name parser —
   * nothing here strips generic "create a schedule" wording.
   *
   * Nothing is created by calling this: the editor's own save dispatches
   * `createScheduleServerCall`, the same call the Database → Time schedule page
   * ends up making.
   */
  private createTimeScheduleTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.TimeSchedules.CreateParams,
    IntelligenceModels.TimeSchedules.CreateDraftResult
  > {
    return {
      name: 'create-time-schedule',
      description:
        'Open the time schedule creation form in the results panel. ' +
        'Use for every request to create, add, or make a new time schedule or weekly schedule. ' +
        'Provide name ONLY when the user supplied the exact name they want, stripped of generic ' +
        'wording such as "create", "a", "new", "time schedule" or "schedule" — ' +
        '"create a schedule called Night shift" is name "Night shift". ' +
        'Omit name entirely when the user only asked to create a schedule without naming it; ' +
        'never invent, translate, shorten or guess a name. ' +
        'Do not ask for the days, hours or time ranges — the form collects them on a week grid. ' +
        'Do not use list-time-schedules for creation requests. ' +
        'After calling, tell the user the creation form is ready and that nothing has been ' +
        'created until they press Create schedule.',
      parameters: z.object({
        name: z
          .string()
          .optional()
          .describe(
            'Exact name the user asked the new time schedule to have, without the surrounding ' +
              'generic create/schedule wording. Omit when the user named nothing.',
          ),
      }),
      handler: async (args: IntelligenceModels.TimeSchedules.CreateParams) => {
        const name = args.name?.trim() ?? '';
        this.resultsService.setCreateTimeSchedule({
          name,
          schedule: IntelligenceModels.TimeSchedules.blankSchedule(),
        });

        return {
          panelUpdated: true,
          draftCreated: true,
          name: name || null,
          message: name
            ? `The time schedule creation form is open, named "${name}". Nothing has been created yet — the user must press Create schedule.`
            : 'The time schedule creation form is open. Nothing has been created yet — the user must fill it in and press Create schedule.',
        } as const;
      },
    };
  }

  // ── create-event-tag ──────────────────────────────────────────────────────

  /**
   * Open the create-event-tag editor, seeded with the name the user asked for.
   * The MCP twin (`EventTagsToolService`) returns the same draft marker for
   * external hosts, so keep the two descriptions in step: the description is the
   * only name parser — nothing here strips generic "create an event tag" wording.
   *
   * Nothing is created by calling this: the editor's own Save dispatches
   * `addCustomEvent`, exactly as the Database → Event tags page does.
   */
  private createEventTagTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.EventTags.CreateParams,
    IntelligenceModels.EventTags.CreateDraftResult
  > {
    return {
      name: 'create-event-tag',
      description:
        'Open the event tag creation form in the results panel. ' +
        'Use for every request to create, add, or make a new event tag or custom event type. ' +
        'Provide name ONLY when the user supplied the exact name they want, stripped of generic ' +
        'wording such as "create", "an", "event tag", "event type" or "event tag name" — ' +
        '"create an event tag called Door forced" is name "Door forced". ' +
        'Omit name entirely when the user only asked to create an event tag without naming it; ' +
        'never invent, translate, shorten or guess a name. ' +
        'Do not ask for the video length or the custom fields — the form collects them. ' +
        'Do not use list-event-tags for creation requests. ' +
        'After calling, tell the user the creation form is ready and that nothing has been ' +
        'created until they press Save.',
      parameters: z.object({
        name: z
          .string()
          .optional()
          .describe(
            'Exact name the user asked the new event tag to have, without the surrounding generic ' +
              'create/event-tag wording. Omit when the user named nothing.',
          ),
      }),
      handler: async (args: IntelligenceModels.EventTags.CreateParams) => {
        const name = args.name?.trim() ?? '';
        this.resultsService.setCreateEventTag({
          customEvent: IntelligenceModels.EventTags.blankCustomEvent(name),
        });

        return {
          panelUpdated: true,
          draftCreated: true,
          name: name || null,
          message: name
            ? `The event tag creation form is open, named "${name}". Nothing has been created yet — the user must press Save.`
            : 'The event tag creation form is open. Nothing has been created yet — the user must fill it in and press Save.',
        };
      },
    };
  }

  // ── prepare-create-tag / create-tag ───────────────────────────────────────

  private prepareCreateTagTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.Tags.Create.Params,
    IntelligenceModels.Tags.Create.PrepareResponse
  > {
    return {
      name: IntelligenceModels.Tags.Create.ToolName.Prepare,
      description: IntelligenceModels.Tags.Create.PREPARE_CREATE_TOOL_DESCRIPTION,
      parameters: z.object({
        name: z.string().optional().describe('Tag name supplied by the user.'),
        color: z
          .string()
          .optional()
          .describe('Tag colour as a six-digit hex string, for example #667085.'),
        isShownInLeftPanel: z
          .boolean()
          .optional()
          .describe(
            'Whether the tag appears in the left panel. False is a real answer, not a default.',
          ),
      }),
      handler: async (
        args: IntelligenceModels.Tags.Create.Params,
      ): Promise<IntelligenceModels.Tags.Create.PrepareResponse> => {
        try {
          return await firstValueFrom(
            this.httpClient.post<IntelligenceModels.Tags.Create.PrepareResponse>(
              api.intelligence.tagsPrepareCreate,
              args,
            ),
          );
        } catch (error: unknown) {
          return {
            error: this.buildTagToolError(error, [
              'Call prepare-create-tag again with the values the user supplied.',
            ]),
          };
        }
      },
    };
  }

  private createTagTool(): IntelligenceModels.FrontendTool<
    IntelligenceModels.Tags.Create.ExecuteParams,
    IntelligenceModels.Tags.Create.ToolResponse
  > {
    return {
      name: IntelligenceModels.Tags.Create.ToolName.Create,
      description: IntelligenceModels.Tags.Create.CREATE_TOOL_DESCRIPTION,
      parameters: z.object({
        preparation: z
          .string()
          .min(1)
          .describe('Preparation token returned by prepare-create-tag.'),
      }),
      handler: async (
        args: IntelligenceModels.Tags.Create.ExecuteParams,
      ): Promise<IntelligenceModels.Tags.Create.ToolResponse> => {
        let result: IntelligenceModels.Tags.Create.ExecuteResponse;
        try {
          result = await firstValueFrom(
            this.httpClient.post<IntelligenceModels.Tags.Create.ExecuteResponse>(
              api.intelligence.tagsCreate,
              args,
            ),
          );
        } catch (error: unknown) {
          return {
            error: this.buildTagToolError(error, [
              'Check the tags list before retrying, in case the tag was created.',
              'If the preparation token expired, call prepare-create-tag again to get a fresh one.',
            ]),
          };
        }

        if (result.error) return { error: result.error };
        // Only a fully-formed success updates the panel — a partial response must
        // never be reported to the user as a created tag.
        if (
          !result.tagCreated ||
          !result.created ||
          !Array.isArray(result.tags) ||
          typeof result.count !== 'number' ||
          typeof result.message !== 'string'
        ) {
          return {
            error: {
              code: IntelligenceModels.Tags.Create.ErrorCode.Upstream,
              message: 'The tags service returned an incomplete creation response.',
              recovery: ['Check the tags list before trying again.'],
            },
          };
        }

        const response: IntelligenceModels.Tags.ListResponse = {
          tags: result.tags,
          count: result.count,
        };
        this.resultsService.setTags(response, `${response.count} tags`);
        return {
          panelUpdated: true,
          tagCreated: true,
          name: result.created.name,
          message: result.message,
          count: result.count,
          assistant: result.assistant,
        };
      },
    };
  }

  private buildTagToolError(
    error: unknown,
    recovery: string[],
  ): IntelligenceModels.Tags.Create.Error {
    return {
      code: IntelligenceModels.Tags.Create.ErrorCode.Upstream,
      message:
        error instanceof Error ? error.message : 'Could not reach the tags service.',
      recovery,
    };
  }

  // ── simple-search-person ──────────────────────────────────────────────────

  private searchPersonTool() {
    return {
      name: 'simple-search-person',
      description:
        'Search for persons in camera footage and display results in the preview panel. ' +
        'Use for any prompt about finding people in cameras without a specific shirt-color filter. ' +
        'Optional personId for face-based search from get-persons results. ' +
        'Response includes a results array with idBase, idIndex, cameraId, and _fc for each match — use these to call expand-search-face (_fc=true) or expand-search-appearance (_fc=false).',
      parameters: z.object({
        personId: z
          .number()
          .optional()
          .describe('Person ID from get-persons for face-based search.'),
        hoursBack: z
          .number()
          .optional()
          .describe('Hours back from now (default 72).'),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
        cameraNames: z.array(z.string()).optional(),
        cameraIds: z.array(z.string()).optional(),
      }),
      handler: async (args: {
        personId?: number;
        hoursBack?: number;
        startTime?: string;
        endTime?: string;
        cameraNames?: string[];
        cameraIds?: string[];
      }) => {
        return this.runSearch('person', args);
      },
    };
  }

  // ── simple-search-person-by-shirt ─────────────────────────────────────────

  private searchPersonByShirtTool() {
    return {
      name: 'simple-search-person-by-shirt',
      description:
        'Search for persons by shirt/upper-body color in camera footage. ' +
        'Use WHENEVER the prompt mentions a shirt color: black, blue, brown, green, grey, orange, pink, purple, red, white, yellow. ' +
        'Response includes a results array with idBase, idIndex, cameraId, and _fc for each match — use these to call expand-search-face (_fc=true) or expand-search-appearance (_fc=false) on any result.',
      parameters: z.object({
        shirtColor: z.string().describe('Shirt color.'),
        hoursBack: z.number().optional(),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
        cameraNames: z.array(z.string()).optional(),
        cameraIds: z.array(z.string()).optional(),
      }),
      handler: async (args: {
        shirtColor: string;
        hoursBack?: number;
        startTime?: string;
        endTime?: string;
        cameraNames?: string[];
        cameraIds?: string[];
      }) => {
        return this.runSearch('person/shirt', {
          shirtColor: args.shirtColor,
          ...args,
        });
      },
    };
  }

  // ── simple-search-vehicle ─────────────────────────────────────────────────

  private searchVehicleTool() {
    return {
      name: 'simple-search-vehicle',
      description:
        'Search for vehicles by color and/or make without a license plate.',
      parameters: z.object({
        vehicleColor: z.string().optional(),
        vehicleMake: z.string().optional(),
        hoursBack: z.number().optional(),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
        cameraNames: z.array(z.string()).optional(),
        cameraIds: z.array(z.string()).optional(),
      }),
      handler: async (args: Record<string, unknown>) => {
        return this.runSearch('vehicle', args);
      },
    };
  }

  // ── simple-search-vehicle-by-plate ────────────────────────────────────────

  private searchVehicleByPlateTool() {
    return {
      name: 'simple-search-vehicle-by-plate',
      description:
        'Search for vehicles by license plate (partial match). Use WHENEVER the prompt mentions a plate.',
      parameters: z.object({
        licensePlate: z.string(),
        hoursBack: z.number().optional(),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
        cameraNames: z.array(z.string()).optional(),
        cameraIds: z.array(z.string()).optional(),
      }),
      handler: async (args: Record<string, unknown>) => {
        return this.runSearch('vehicle/plate', args);
      },
    };
  }

  // ── smart-search ──────────────────────────────────────────────────────────

  private smartSearchTool() {
    return {
      name: 'smart-search',
      description:
        'Universal free-text search in camera footage. Call it immediately with the visual description and any camera/time information the user gave; do not rewrite the visual query. Cameras and a time range are required. If the response has needsInput, ask exactly that one question or call pick-cameras; never invent a scope or search all cameras.',
      parameters: z.object({
        query: z.string().describe('The complete search query.'),
        hoursBack: z.number().optional(),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
        cameraNames: z.array(z.string()).optional(),
        cameraIds: z.array(z.string()).optional(),
        latestTs: z.number().optional(),
        size: z.number().int().positive().max(100).optional(),
      }),
      handler: async (args: {
        query: string;
        hoursBack?: number;
        startTime?: string;
        endTime?: string;
        cameraNames?: string[];
        cameraIds?: string[];
        latestTs?: number;
        size?: number;
      }) => {
        let params = this.buildSearchParams(args);
        params = params.set('query', args.query);
        const response = await firstValueFrom(
          this.httpClient.get<IntelligenceModels.Search.Response>(
            `${api.intelligence.search}/smart`,
            { params },
          ),
        );
        if (response.needsInput) {
          return { panelUpdated: false, needsInput: response.needsInput, success: response.success };
        }
        const label = `${response.count ?? response.results?.length ?? 0} results · smart`;
        this.resultsService.setSearch(response, label);
        return {
          panelUpdated: true,
          count: response.count,
          more: response.more,
          latestTs: response.latestTs,
          filter: response.filter,
          success: response.success,
          results: this.summariseResults(response.results),
        };
      },
    };
  }

  // ── expand-search-face ────────────────────────────────────────────────────

  private expandSearchFaceTool() {
    return {
      name: 'expand-search-face',
      description:
        'Find every camera appearance of a KNOWN person via face matching. ' +
        'Use ONLY after identifying a specific person from a prior search result — read idBase, idIndex, cameraId from the results array returned by the search tool (NEVER invent or guess these values). ' +
        'Only use when _fc=true for that result. ' +
        'After calling this tool write a short reply summarising the match count.',
      parameters: z.object({
        idBase: z.number().describe('idBase from the prior search result.'),
        idIndex: z.number().describe('idIndex from the prior search result.'),
        cameraId: z.string().describe('cameraId from the prior search result.'),
        timeRangeHours: z
          .number()
          .optional()
          .describe('Hours to search (default: 24).'),
        threshold: z
          .number()
          .optional()
          .describe('Similarity threshold 0-1 (default backend value).'),
        cameraIds: z.array(z.string()).optional(),
        cameraNames: z.array(z.string()).optional(),
      }),
      handler: async (args: {
        idBase: number;
        idIndex: number;
        cameraId: string;
        timeRangeHours?: number;
        threshold?: number;
        cameraIds?: string[];
        cameraNames?: string[];
      }) => {
        return this.runExpandSearch('expand/face', args);
      },
    };
  }

  // ── expand-search-appearance ──────────────────────────────────────────────

  private expandSearchAppearanceTool() {
    return {
      name: 'expand-search-appearance',
      description:
        'Find similar appearances of an UNKNOWN person by clothing/body re-identification. ' +
        'Use when _fc=false for the target result — read idBase, idIndex, cameraId from the results array returned by the search tool (NEVER invent or guess these values). ' +
        'After calling this tool write a short reply summarising the match count.',
      parameters: z.object({
        idBase: z.number().describe('idBase from the prior search result.'),
        idIndex: z.number().describe('idIndex from the prior search result.'),
        cameraId: z.string().describe('cameraId from the prior search result.'),
        timeRangeHours: z
          .number()
          .optional()
          .describe('Hours to search (default: 24).'),
        threshold: z
          .number()
          .optional()
          .describe('Similarity threshold 0-1 (default backend value).'),
        cameraIds: z.array(z.string()).optional(),
        cameraNames: z.array(z.string()).optional(),
      }),
      handler: async (args: {
        idBase: number;
        idIndex: number;
        cameraId: string;
        timeRangeHours?: number;
        threshold?: number;
        cameraIds?: string[];
        cameraNames?: string[];
      }) => {
        return this.runExpandSearch('expand/appearance', args);
      },
    };
  }

  // ── create-alert-configuration ─────────────────────────────────────────────

  private createAlertConfigurationTool() {
    return {
      name: 'create-alert-configuration',
      description:
        'Open an alert-configuration draft in the preview panel. ' +
        'Use for every request to create, configure, or add a new alert or alert configuration. ' +
        'Provide name only when the user supplied a desired configuration name. ' +
        'Provide template only when the user named an alert template or alert type. Preserve the user\'s exact template phrase; never translate, shorten, substitute, or choose a closest template. Remove only generic alert/configuration wording around it. ' +
        'The tool resolves exact template names and one unambiguous partial match. Unknown, ambiguous, hidden, or disabled templates open the template picker instead. ' +
        'The user must complete the draft and click Save before anything is created. ' +
        'Do not use search-alerts or list-alert-configurations for creation requests. ' +
        'After calling, tell the user whether the editor or template picker is ready.',
      parameters: z.object({
        template: z
          .string()
          .optional()
          .describe(
            'Verbatim alert template/type phrase supplied by the user. Do not translate, shorten, substitute, or choose a closest template; exclude only surrounding generic alert/configuration words.',
          ),
        name: z
          .string()
          .optional()
          .describe(
            'Exact name requested for the new alert configuration.',
          ),
      }),
      handler: async (
        args: IntelligenceModels.AlertConfigurations.CreateParams,
      ) => {
        const draft: IntelligenceModels.AlertConfigurations.CreateAlertDraft = {
          template: null,
          name: args.name,
        };
        const createLabel = this.translateService.instant(
          'SEARCH_GLOBAL_MODAL.ALERT_CONFIGURATIONS.CREATE_ALERT',
        );
        this.resultsService.setAlertTemplatesDraft(draft, createLabel);

        const templateQuery = args.template?.trim() ?? '';
        const match = templateQuery
          ? resolveAlertTemplate(
              templateQuery,
              (key) => this.translateService.instant(key),
              this.getAlertTemplateAvailability(),
            )
          : null;

        if (match) {
          const isPanicTemplate =
            match.template.category === AlertCategory.System && match.template.flowType === SystemType.Panic;
          let configuration: EventV2Document | undefined;
          if (isPanicTemplate) {
            try {
              configuration =
                (await firstValueFrom(this.panicSystemConfigurationService.getPanicEvent().pipe(take(1)))) ??
                undefined;
            } catch {
              configuration = undefined;
            }
          }

          this.resultsService.setOverlay(
            { ...draft, template: match.template, configuration },
            'create-alert',
            match.label,
          );
        }

        return {
          panelUpdated: true,
          draftCreated: true,
          name: args.name ?? null,
          requestedTemplate: templateQuery || null,
          matchedTemplate: match?.label ?? null,
          templateResolved: !!match,
          message: match
            ? `The ${match.label} alert draft is open. The user can finish the configuration and click Save, or Cancel to choose another template.`
            : 'The alert template picker is open. The user can choose a template, finish the configuration, and click Save.',
        };
      },
    };
  }

  private getAlertTemplateAvailability(): IntelligenceModels.AlertConfigurations.TemplateAvailability {
    return {
      hasProtectiveGear: !!this.hasProtectiveGear(),
      hasDoors: !!this.savedDoors()?.length,
      hasShelves: !!this.hasShelves(),
      isDeveloper: !!this.isDeveloper(),
      permissions: this.permissions() as PermissionModel.UserPermissionsState,
      hasAccessDoors: !!this.hasAccessDoors(),
      violenceEnabled: !!this.activeOrganization()?.violenceEventEnabled,
      shelvesEnabled: !!this.shelvesEnabled(),
      hasObjectStates: !!this.objectStates()?.length,
      hasPhysicalAccessControls: !!this.physicalAccessControls()?.length,
      faceRecognitionEnabled: !!this.faceRecognitionEnabled(),
      enablePalestinianLicensePlate:
        !!this.enablePalestinianLicensePlate(),
      disableMissingObjectEventConfiguration:
        !!this.disableMissingObjectEventConfiguration(),
    };
  }

  // ── search-alerts ─────────────────────────────────────────────────────────

  private alertsTool() {
    return {
      name: 'search-alerts',
      description:
        'Search alerts in the preview panel. ' +
        'Use for ANY prompt about alerts, notifications, triggers, events, incidents, or "what was triggered". ' +
        'Do not use for requests to create or configure an alert; use create-alert-configuration. ' +
        'Response includes an alerts array with alertId, eventName, cameraName, and localTime for each result — use alertId to call get-alert for full details on a specific alert. ' +
        'After calling this tool write a short reply summarising the alert count and top event types.',
      parameters: z.object({
        hoursBack: z.number().optional().describe('Hours back (default: 24).'),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
        cameraIds: z.array(z.string()).optional(),
        cameraNames: z.array(z.string()).optional(),
        alertTypes: z
          .array(z.string())
          .optional()
          .describe('Alert type/template names, resolved server-side.'),
      }),
      handler: async (args: {
        hoursBack?: number;
        startTime?: string;
        endTime?: string;
        cameraIds?: string[];
        cameraNames?: string[];
        alertTypes?: string[];
      }) => {
        const response = await this.fetchAlerts({
          ...args,
          size: IntelligenceModels.Alerts.DEFAULT_LIMIT,
        });
        const label = `${response.totalCount ?? 0} alerts`;
        this.resultsService.setAlerts(response, label);
        return {
          panelUpdated: true,
          totalCount: response.totalCount,
          success: response.success,
          topN: response.topN,
          alerts: response.results?.map((a, i) => ({
            index: i,
            alertId: a.alertId,
            eventName: a.eventName,
            cameraName: a.cameraName,
            localTime: a.localTime,
          })),
        };
      },
    };
  }

  // ── get-alert ─────────────────────────────────────────────────────────────

  private getAlertTool() {
    return {
      name: 'get-alert',
      description:
        'Fetch and display a single alert by its ID in the preview panel. ' +
        'Use when the user asks about a specific alert or when following up on a specific alertId from search-alerts results. ' +
        'After calling this tool write a short reply with the alert name and timestamp.',
      parameters: z.object({
        alertId: z.string().describe('The alert ID to retrieve.'),
      }),
      handler: async ({ alertId }: { alertId: string }) => {
        const response = await firstValueFrom(
          this.httpClient.get<{
            success: boolean;
            alert?: IntelligenceModels.Alerts.Item;
            error?: string;
          }>(`${api.intelligence.alerts}/${alertId}`),
        );
        if (!response.success || !response.alert) {
          return {
            panelUpdated: false,
            error: response.error ?? 'Alert not found',
          };
        }
        const wrapped: IntelligenceModels.Alerts.Response = {
          success: true,
          totalCount: 1,
          results: [response.alert],
        };
        this.resultsService.setAlerts(
          wrapped,
          `Alert · ${response.alert.eventName ?? alertId}`,
        );
        return {
          panelUpdated: true,
          alertId,
          eventName: response.alert.eventName,
        };
      },
    };
  }

  // ── list-alert-configurations ───────────────────────────────────────────────

  /**
   * Fetch one page of alert configurations from the intelligence endpoint. Shared by
   * the `list-alert-configurations` tool handler and the alert-configurations-result
   * component's filtering/pagination. `hasMore` is derived client-side: a full page
   * means more may exist.
   */
  public async fetchAlertConfigurations(
    params: IntelligenceModels.AlertConfigurations.ListParams,
  ): Promise<IntelligenceModels.AlertConfigurations.Response> {
    let httpParams = new HttpParams();
    if (params.limit) httpParams = httpParams.set('limit', params.limit);
    if (params.query?.trim())
      httpParams = httpParams.set('query', params.query.trim());
    if (params.lastId) httpParams = httpParams.set('lastId', params.lastId);
    // Arrays are sent comma-delimited to match the endpoint's class-transformer DTO.
    if (params.cameraIdsIncludes?.length)
      httpParams = httpParams.set(
        'cameraIdsIncludes',
        params.cameraIdsIncludes.join(','),
      );
    if (params.cameraIdsExcludes?.length)
      httpParams = httpParams.set(
        'cameraIdsExcludes',
        params.cameraIdsExcludes.join(','),
      );
    if (params.tagIds?.length)
      httpParams = httpParams.set('tagIds', params.tagIds.join(','));
    // flowTypes is an array of {category, flowType} — sent JSON-encoded (the endpoint parses it).
    if (params.flowTypes?.length)
      httpParams = httpParams.set(
        'flowTypes',
        JSON.stringify(params.flowTypes),
      );
    // Name filters come from the assistant, which only has what the user said. JSON-encoded
    // rather than comma-joined because camera and tag names may contain commas.
    if (params.cameraNames?.length)
      httpParams = httpParams.set(
        'cameraNames',
        JSON.stringify(params.cameraNames),
      );
    if (params.tagNames?.length)
      httpParams = httpParams.set('tagNames', JSON.stringify(params.tagNames));
    if (params.alertTypes?.length)
      httpParams = httpParams.set(
        'alertTypes',
        JSON.stringify(params.alertTypes),
      );

    const response = await firstValueFrom(
      this.httpClient.get<IntelligenceModels.AlertConfigurations.Response>(
        api.intelligence.alertConfigurations,
        { params: httpParams },
      ),
    );

    return { ...response, hasMore: response.count >= response.filter.limit };
  }

  /** Fetch the complete configuration before editing; list rows intentionally omit heavy fields. */
  public async fetchAlertConfiguration(
    eventId: string,
  ): Promise<EventV2Document> {
    return firstValueFrom(
      this.httpClient.get<EventV2Document>(api.alertEvents.one(eventId)),
    );
  }

  /**
   * Delete an alert configuration (used by the alert-configurations-result row menu).
   * Branches to the zone-region endpoint when the config carries zone regions, mirroring
   * the alert-events effect.
   */
  public deleteAlertConfiguration(event: EventV2Document): Observable<unknown> {
    const id = event._id ?? '';
    return this.hasZoneRegions(event)
      ? this.httpClient.delete(api.alertEvents.removeWithZoneRegions(id))
      : this.httpClient.delete(api.alertEvents.remove(id));
  }

  /**
   * Enable/disable an alert configuration (used by the row toggle). Branches to the
   * zone-region endpoint when the config carries zone regions.
   */
  public toggleAlertConfiguration(
    event: EventV2Document,
    enabled: boolean,
  ): Observable<unknown> {
    const id = event._id ?? '';
    const hasZoneRegions = this.hasZoneRegions(event);
    if (enabled) {
      return this.httpClient.put(
        hasZoneRegions
          ? api.alertEvents.enableWithZoneRegions(id)
          : api.alertEvents.enable(id),
        {},
      );
    }
    return this.httpClient.put(
      hasZoneRegions
        ? api.alertEvents.disableWithZoneRegions(id)
        : api.alertEvents.disable(id),
      {},
    );
  }

  /**
   * True when the config's flow carries zone regions. `formValue` is a union where only
   * some members declare `zoneRegionsIds`, so read it via a cast (mirrors the alert-events effect).
   */
  private hasZoneRegions(event: EventV2Document): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const zoneRegionsIds = (event.selectedFlow?.formValue as any)
      ?.zoneRegionsIds;
    return Array.isArray(zoneRegionsIds) && !!zoneRegionsIds.length;
  }

  private alertConfigurationsTool() {
    return {
      name: 'list-alert-configurations',
      description:
        "Fetch and display the organization's alert configurations — the alert rules/setups a user has " +
        'created — in the preview panel. ' +
        'Use for requests to show, list, or browse configured alerts, alert rules, alert setups, ' +
        'or alert configurations (e.g. "show me all alert configurations"). ' +
        'Also filters by camera, alert type, and tag: "show me all alert configurations related to ' +
        '<camera>" → cameraNames, "show me all <type> alert configurations" → alertTypes, ' +
        '"...tagged <tag>" → tagNames. Put the subject in the matching filter, not in query — query ' +
        'only matches the name the user gave the configuration. ' +
        'Do not use for requests to create a configuration; use create-alert-configuration. ' +
        'Do not use this tool to enable or disable a configuration; use set-alert-configuration-enabled. ' +
        'This is NOT for triggered alert instances — use search-alerts for "what was triggered / notifications". ' +
        'Supports a name query, a result limit, and a lastId cursor for pagination. ' +
        'After calling this tool ALWAYS write a short text reply summarising what was found; if the ' +
        'result reports an unresolved name, tell the user that name matched nothing.',
      parameters: z.object({
        limit: z
          .number()
          .int()
          .positive()
          .optional()
          .describe(
            'Maximum number of alert configurations to return. Default 100.',
          ),
        query: z
          .string()
          .optional()
          .describe('Alert configuration name search query.'),
        lastId: z
          .string()
          .optional()
          .describe(
            'Return configurations whose _id is older than this ObjectId (cursor pagination).',
          ),
        cameraNames: z
          .array(z.string())
          .optional()
          .describe(
            'Camera names exactly as the user said them (partial match, resolved server-side). ' +
              'Do not translate, shorten, or substitute a closest camera.',
          ),
        alertTypes: z
          .array(z.string())
          .optional()
          .describe(
            'Alert type/template names exactly as the user said them, for example "Tampering" or ' +
              '"Camera status" (resolved server-side against the alert template catalogue). Strip only ' +
              'surrounding generic alert/configuration wording; never substitute a closest type.',
          ),
        tagNames: z
          .array(z.string())
          .optional()
          .describe(
            'Organization tag names exactly as the user said them (partial match, resolved server-side).',
          ),
      }),
      handler: async (
        args: IntelligenceModels.AlertConfigurations.ListParams,
      ) => {
        const response = await this.fetchAlertConfigurations(args);
        // The panel's filter bar is seeded from uiState, so a prompt-driven filter shows up
        // as chips the user can drop — without it the rows would look narrowed for no reason.
        const withUiState: IntelligenceModels.AlertConfigurations.Response = {
          ...response,
          uiState: buildAlertConfigurationsUiState(response.filter),
        };
        const label = `${response.count} alert configs${buildAlertConfigurationsLabelSuffix(response.filter)}`;
        this.resultsService.setAlertConfigurations(withUiState, label);
        return {
          panelUpdated: true,
          count: response.count,
          filter: response.filter,
          message: response.message,
        };
      },
    };
  }

  /**
   * Enable/disable alert configurations, then show the affected ones in the preview
   * panel. The endpoint returns them as a list response (`enabledChanged` marker),
   * so the panel is refreshed from the same round trip.
   */
  private setAlertConfigurationEnabledTool() {
    return {
      name: 'set-alert-configuration-enabled',
      description:
        'Enable or disable one or more alert configurations by name, or every visible alert configuration. ' +
        'Use immediately for explicit requests such as "disable Panic and Lobby alerts" or "enable all alert configurations"; ' +
        'no application-level confirmation is required. Provide exactly one of name, names, or all. ' +
        'Matching is literal and case-insensitive: an exact name wins, otherwise one partial match is accepted. ' +
        'Ambiguous matches and already-correct states do not change anything. Individual failures do not stop the batch. ' +
        'The affected alert configurations are listed in the preview panel afterwards. ' +
        'Always relay the returned message to the user.',
      parameters: z
        .object({
          name: z
            .string()
            .min(1)
            .optional()
            .describe('Deprecated single name selector.'),
          names: z
            .array(z.string().min(1))
            .min(1)
            .optional()
            .describe(
              'Alert configuration names, each of which may be exact or partial.',
            ),
          all: z
            .literal(true)
            .optional()
            .describe(
              'Set true to change every alert configuration visible to the caller.',
            ),
          enabled: z
            .boolean()
            .describe('True to enable configurations; false to disable them.'),
        })
        .refine(
          (params) =>
            [
              params.name !== undefined,
              params.names !== undefined,
              params.all !== undefined,
            ].filter(Boolean).length === 1,
          { message: 'Provide exactly one of name, names, or all.' },
        ),
      handler: async (
        args: IntelligenceModels.AlertConfigurations.SetEnabledParams,
      ): Promise<IntelligenceModels.AlertConfigurations.SetEnabledToolResponse> => {
        const result = await firstValueFrom(
          this.httpClient.put<IntelligenceModels.AlertConfigurations.SetEnabledResponse>(
            api.intelligence.alertConfigurationStatus,
            args,
          ),
        );
        const toolResponse: IntelligenceModels.AlertConfigurations.SetEnabledToolResponse =
          {
            results: result.results,
            summary: result.summary,
            message: result.message,
          };

        // An older backend omits the list block; the state change still succeeded,
        // so report it instead of failing the tool call.
        if (
          result.enabledChanged !== true ||
          !Array.isArray(result.items) ||
          typeof result.count !== 'number' ||
          !result.filter
        ) {
          return toolResponse;
        }

        const response: IntelligenceModels.AlertConfigurations.Response = {
          items: result.items,
          count: result.count,
          filter: result.filter,
          message: result.message,
          hasMore: result.count >= result.filter.limit,
        };
        const label = `${response.count} alert configs${result.filter.query ? ` · ${result.filter.query}` : ''}`;
        this.resultsService.setAlertConfigurations(response, label);
        return { panelUpdated: true, ...toolResponse };
      },
    };
  }

  // ── Chart tools ───────────────────────────────────────────────────────────

  private buildChartTool(name: string, description: string, chartPath: string) {
    return {
      name,
      description: `${description} Results displayed in the preview panel as a chart.`,
      parameters: z.object({
        timeframe: z
          .enum([
            'last_24_hours',
            'same_day_last_week',
            'last_week',
            'last_month',
          ])
          .optional()
          .describe('Time period (default: last_24_hours).'),
        objectTypes: z
          .array(z.enum(['person', 'vehicle', 'animal']))
          .optional(),
        cameraNames: z.array(z.string()).optional(),
        cameraIds: z.array(z.string()).optional(),
      }),
      handler: async (args: {
        timeframe?: string;
        objectTypes?: string[];
        cameraNames?: string[];
        cameraIds?: string[];
      }) => {
        const label = `${name.replace('generate-', '')} · ${args.timeframe ?? 'last_24_hours'}`;
        try {
          let params = new HttpParams();
          if (args.timeframe) params = params.set('timeframe', args.timeframe);
          // Normalise objectTypes — LLM may pass a string when only one type is provided
          const objectTypes = args.objectTypes
            ? Array.isArray(args.objectTypes)
              ? args.objectTypes
              : [args.objectTypes]
            : [];
          objectTypes.forEach((t) => {
            params = params.append('objectTypes', t);
          });
          if (args.cameraNames?.length)
            args.cameraNames.forEach((n) => {
              params = params.append('cameraNames', n);
            });
          if (args.cameraIds?.length)
            args.cameraIds.forEach((id) => {
              params = params.append('cameraIds', id);
            });
          const response = await firstValueFrom(
            this.httpClient.get<IntelligenceModels.Charts.Response>(
              `${api.intelligence.charts}/${chartPath}`,
              { params },
            ),
          );
          this.resultsService.setChart(response, label);
          return {
            panelUpdated: true,
            plotType: response.plotType,
            timeframe: response.timeframe,
            success: response.success,
          };
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err);
          console.error(`[ChartTool:${name}]`, message, err);
          // Surface a failed-chart in the panel so the user sees something instead
          // of a raw error string in the chat bubble. No `error` text: the panel
          // renders its own translated failure message when the chart carries none.
          const errorResponse: IntelligenceModels.Charts.Response = {
            success: false,
          };
          this.resultsService.setChart(errorResponse, label);
          return { panelUpdated: true, success: false, error: message };
        }
      },
    };
  }

  private objectDetectionsByHourTool() {
    return this.buildChartTool(
      'generate-object-detections-by-hour',
      'Chart: total object detections over time (hourly/daily).',
      'object-detections-by-hour',
    );
  }
  private objectDetectionsByCameraTool() {
    return this.buildChartTool(
      'generate-object-detections-by-camera',
      'Chart: total object detections per camera.',
      'object-detections-by-camera',
    );
  }
  private objectGroupsByHourTool() {
    return this.buildChartTool(
      'generate-object-groups-by-hour',
      'Chart: detections over time broken down by object group (Person, Vehicle, etc.).',
      'object-groups-by-hour',
    );
  }
  private objectTypesByHourTool() {
    return this.buildChartTool(
      'generate-object-types-by-hour',
      'Chart: detections over time broken down by specific object type (Car, Motorcycle, etc.).',
      'object-types-by-hour',
    );
  }
  private objectTypesByCameraTool() {
    return this.buildChartTool(
      'generate-object-types-by-camera',
      'Chart: detections per camera broken down by object type.',
      'object-types-by-camera',
    );
  }
  private alertsByHourTool() {
    return this.buildChartTool(
      'generate-alerts-by-hour',
      'Chart: total alert events over time (hourly/daily).',
      'alerts-by-hour',
    );
  }
  private alertsByCameraTool() {
    return this.buildChartTool(
      'generate-alerts-by-camera',
      'Chart: total alert events per camera.',
      'alerts-by-camera',
    );
  }
  private alertTypesDistributionTool() {
    return this.buildChartTool(
      'generate-alert-types-distribution',
      'Chart: distribution of alert types (Line Crossing, Intrusion, etc.).',
      'alert-types-distribution',
    );
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private async runExpandSearch(
    endpoint: string,
    args: {
      idBase: number;
      idIndex: number;
      cameraId: string;
      timeRangeHours?: number;
      threshold?: number;
      cameraIds?: string[];
      cameraNames?: string[];
    },
  ): Promise<Record<string, unknown>> {
    let params = new HttpParams()
      .set('idBase', String(args.idBase))
      .set('idIndex', String(args.idIndex))
      .set('cameraId', args.cameraId);
    if (args.timeRangeHours)
      params = params.set('timeRangeHours', String(args.timeRangeHours));
    if (args.threshold != null)
      params = params.set('threshold', String(args.threshold));
    if (Array.isArray(args.cameraNames))
      args.cameraNames.forEach((n) => {
        params = params.append('cameraNames', n);
      });
    if (Array.isArray(args.cameraIds))
      args.cameraIds.forEach((id) => {
        params = params.append('cameraIds', id);
      });
    const response = await firstValueFrom(
      this.httpClient.get<IntelligenceModels.Search.Response>(
        `${api.intelligence.search}/${endpoint}`,
        { params },
      ),
    );
    const kind = endpoint.includes('face') ? 'face' : 'appearance';
    const label = `${response.count ?? response.results?.length ?? 0} matches · ${kind}`;
    this.resultsService.setSearch(response, label);
    return {
      panelUpdated: true,
      count: response.count,
      success: response.success,
      results: this.summariseResults(response.results),
    };
  }

  private async runSearch(
    endpoint: string,
    args: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const params = this.buildSearchParams(args);
    const response = await firstValueFrom(
      this.httpClient.get<IntelligenceModels.Search.Response>(
        `${api.intelligence.search}/${endpoint}`,
        { params },
      ),
    );
    const label = `${response.count ?? response.results?.length ?? 0} results · ${endpoint.split('/').pop()}`;
    this.resultsService.setSearch(response, label);
    return {
      panelUpdated: true,
      count: response.count,
      success: response.success,
      results: this.summariseResults(response.results),
    };
  }

  /**
   * Returns a compact summary of search result items so the LLM has the identifiers
   * (idBase, idIndex, cameraId) needed to call expand-search-face / expand-search-appearance.
   * _fc (face-capture flag) tells the LLM which expand tool to use: true=face, false=appearance.
   * The short, underscore-prefixed name prevents the LLM from quoting it verbatim in responses.
   */
  private summariseResults(
    results: IntelligenceModels.Search.ResultItem[] | undefined,
  ): unknown[] {
    if (!results?.length) return [];
    return results.map((r, i) => ({
      index: i,
      idBase: r.idBase,
      idIndex: r.idIndex,
      cameraId: r.cameraId,
      cameraName: r.cameraName,
      localTime: r.localTime,
      _fc: r.zoomImageUrl != null,
    }));
  }

  private buildSearchParams(args: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    if (args['personId'])
      params = params.set('personId', String(args['personId']));
    if (args['shirtColor'])
      params = params.set('shirtColor', String(args['shirtColor']));
    if (args['vehicleColor'])
      params = params.set('vehicleColor', String(args['vehicleColor']));
    if (args['vehicleMake'])
      params = params.set('vehicleMake', String(args['vehicleMake']));
    if (args['licensePlate'])
      params = params.set('licensePlate', String(args['licensePlate']));
    if (args['hoursBack'])
      params = params.set('hoursBack', String(args['hoursBack']));
    if (args['startTime'])
      params = params.set('startTime', String(args['startTime']));
    if (args['endTime'])
      params = params.set('endTime', String(args['endTime']));
    if (args['latestTs'])
      params = params.set('latestTs', String(args['latestTs']));
    if (args['size'])
      params = params.set('size', String(args['size']));
    if (Array.isArray(args['cameraNames']))
      (args['cameraNames'] as string[]).forEach((n) => {
        params = params.append('cameraNames', n);
      });
    if (Array.isArray(args['cameraIds']))
      (args['cameraIds'] as string[]).forEach((id) => {
        params = params.append('cameraIds', id);
      });
    return params;
  }
}

/**
 * Turn the filter the backend applied into the panel's presentation state, so a
 * prompt-driven filter ("...related to Front Door") lands in the filter bar as chips
 * instead of silently narrowing the rows.
 */
function buildAlertConfigurationsUiState(
  filter: IntelligenceModels.AlertConfigurations.Filter,
): IntelligenceModels.AlertConfigurations.UiState {
  const cameras: IntelligenceModels.AlertConfigurations.UiState['cameras'] = {};
  for (const camera of filter.cameras ?? []) {
    cameras[camera.cameraId] = {
      cameraId: camera.cameraId,
      edgeId: camera.edgeId,
      locationId: camera.locationId,
      name: camera.name,
    };
  }
  return {
    query: filter.query ?? '',
    cameras,
    flowTypes: filter.flowTypes ?? [],
    tagIds: filter.tagIds ?? [],
    scrollOffset: 0,
  };
}

/** History-entry label suffix naming what the result was filtered by. */
function buildAlertConfigurationsLabelSuffix(
  filter: IntelligenceModels.AlertConfigurations.Filter,
): string {
  const parts = [
    filter.query,
    ...(filter.cameras ?? []).map((camera) => camera.name),
    ...(filter.flowTypes ?? []).map((flowType) => flowType.label),
    ...(filter.tags ?? []).map((tag) => tag.name),
  ].filter(Boolean);
  return parts.length ? ` · ${parts.join(', ')}` : '';
}
